// src/screens/DispatchScreen.jsx
import React, { useEffect, useState, useMemo, Fragment, useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';
import { Tab, Dialog, Transition, Popover, Menu } from '@headlessui/react';
import { 
  FiChevronRight, FiChevronDown, FiFilter, FiMessageSquare, FiSearch, 
  FiMapPin, FiClock, FiLock, FiUser, FiPackage, FiTruck, FiCheck,
  FiX, FiMoreHorizontal, FiRefreshCw, FiCommand, FiCalendar,
  FiAlertCircle, FiZap, FiLayers, FiMoon, FiSun, FiGrid, FiList, FiLayout, FiMap,
  FiDollarSign
} from 'react-icons/fi';
import { MapContainer, TileLayer, Marker, CircleMarker, Circle, Polyline, Tooltip, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import request from '../utils/request';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import SelectDropdown from '../components/SelectDropdown';
import DispatchOrderDrawer from '../components/DispatchOrderDrawer';
import AssignDriver from '../components/AssignDriver';
import { getCoordinates } from '../utils/geocode';
import { useResolvedOrderCoords } from '../utils/resolveOrderCoords';
import { formatDate } from '../utils/formatDate';
import useLockBodyScroll from '../utils/useLockBodyScroll';
import { XMarkIcon } from '@heroicons/react/24/outline'
import ChangeDeliveryFee from '../components/ChangeDeliveryFee'
import { canBatch } from '../components/Batching';
import { collection, query, where, onSnapshot } from "firebase/firestore";
import { db } from "../firebase";
import { arrivalInPst } from '../utils/propertyTimezoneToPST';
import { StatePill } from '../utils/statePill';
import {
  hasDispatchDeliveryTime,
  hasDispatchEntryInstructions,
  isAssignDriverOrder,
  isDoorDeliveryOrder,
  needsDispatchAttention,
} from '../utils/dispatchOrderBuckets';
// BulkActionBar removed - using EOD Review and Morning Check instead
import AlertCenter from '../components/AlertCenter';
import PerformanceMetrics from '../components/PerformanceMetrics';
import ActivityTimeline from '../components/ActivityTimeline';
import SavedViews from '../components/SavedViews';
import EODReviewMode from '../components/EODReviewMode';
import MorningCheckMode from '../components/MorningCheckMode';
import { useDarkMode } from '../contexts/DarkModeContext';
import { createOrderSocket, bindOrderSocket } from '../store/thunks/trackingSocket';
import { useDispatch } from 'react-redux';
import { isFullAdmin } from '../utils/adminIds';
import { fetchInquiries } from '../store/thunks/inquiriesActions';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  isTerminalOrderStatus as sharedIsTerminalOrderStatus,
  isOrderWithinNextDaysPST as sharedIsOrderWithinNextDaysPST,
  getPSTDateString as sharedGetPSTDateString,
  getOrderDateInPST as sharedGetOrderDateInPST,
  opsWindowFromParam,
} from '../components/commandCenter/opsStats';

// ═══════════════════════════════════════════════════════════════
// HELPER FUNCTIONS
// ═══════════════════════════════════════════════════════════════

function parseDateFromString(input) {
  if (!input || typeof input !== 'string') return null;
  const trimmed = input.trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) return new Date(trimmed + 'T00:00:00');
  const slashMatch = trimmed.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (slashMatch) {
    const [, month, day, year] = slashMatch;
    return new Date(`${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}T00:00:00`);
  }
  const dashMatch = trimmed.match(/^(\d{1,2})-(\d{1,2})-(\d{4})$/);
  if (dashMatch) {
    const [, month, day, year] = dashMatch;
    return new Date(`${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}T00:00:00`);
  }
  const lower = trimmed.toLowerCase();
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  if (lower === 'today' || lower.includes('today')) return today;
  if (lower === 'tomorrow' || lower.includes('tomorrow')) {
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    return tomorrow;
  }
  const parsed = new Date(trimmed);
  if (!isNaN(parsed.getTime())) {
    parsed.setHours(0, 0, 0, 0);
    return parsed;
  }
  return null;
}

const getPSTDateString = sharedGetPSTDateString;
const getOrderDateInPST = sharedGetOrderDateInPST;
const isTerminalOrderStatus = sharedIsTerminalOrderStatus;
const isOrderWithinNextDaysPST = sharedIsOrderWithinNextDaysPST;

function isPastInStockDateInPST(inStockDate, now = new Date()) {
  const orderDate = getOrderDateInPST(inStockDate);
  if (!orderDate) return false;
  return orderDate < getPSTDateString(now);
}

// ═══════════════════════════════════════════════════════════════
// STAT PILL COMPONENT - Clickable metrics
// ═══════════════════════════════════════════════════════════════

function StatPill({ label, count, color = 'gray', active = false, pulse = false, onClick, icon: Icon }) {
  const colorClasses = {
    coral: active 
      ? 'bg-[#FEE2E2] dark:bg-[#7F1D1D]/40 text-[#B91C1C] dark:text-[#FCA5A5] ring-1 ring-[#EF4444]/50 shadow-[0_4px_12px_rgba(239,68,68,0.15)]' 
      : 'bg-white dark:bg-[#27272A] text-[#B91C1C] dark:text-[#FCA5A5] ring-1 ring-[#E4E4E7] dark:ring-[#3F3F46] hover:ring-[#EF4444]/50',
    amber: active 
      ? 'bg-[#FEF3C7] dark:bg-[#78350F]/40 text-[#92400E] dark:text-[#FCD34D] ring-1 ring-[#F59E0B]/50 shadow-[0_4px_12px_rgba(245,158,11,0.15)]' 
      : 'bg-white dark:bg-[#27272A] text-[#B45309] dark:text-[#FCD34D] ring-1 ring-[#E4E4E7] dark:ring-[#3F3F46] hover:ring-[#F59E0B]/50',
    emerald: active 
      ? 'bg-[#D1FAE5] dark:bg-[#064E3B]/40 text-[#065F46] dark:text-[#6EE7B7] ring-1 ring-[#10B981]/50 shadow-[0_4px_12px_rgba(16,185,129,0.15)]' 
      : 'bg-white dark:bg-[#27272A] text-[#047857] dark:text-[#6EE7B7] ring-1 ring-[#E4E4E7] dark:ring-[#3F3F46] hover:ring-[#10B981]/50',
    violet: active 
      ? 'bg-[#EDE9FE] dark:bg-[#4C1D95]/40 text-[#5B21B6] dark:text-[#C4B5FD] ring-1 ring-[#8B5CF6]/50 shadow-[0_4px_12px_rgba(139,92,246,0.15)]' 
      : 'bg-white dark:bg-[#27272A] text-[#6D28D9] dark:text-[#C4B5FD] ring-1 ring-[#E4E4E7] dark:ring-[#3F3F46] hover:ring-[#8B5CF6]/50',
    sky: active 
      ? 'bg-[#DBEAFE] dark:bg-[#1E3A5F]/40 text-[#1E40AF] dark:text-[#93C5FD] ring-1 ring-[#3B82F6]/50 shadow-[0_4px_12px_rgba(59,130,246,0.15)]' 
      : 'bg-white dark:bg-[#27272A] text-[#1D4ED8] dark:text-[#93C5FD] ring-1 ring-[#E4E4E7] dark:ring-[#3F3F46] hover:ring-[#3B82F6]/50',
    gray: active 
      ? 'bg-[#F4F4F5] dark:bg-[#3F3F46] text-[#18181B] dark:text-white ring-1 ring-[#A1A1AA] dark:ring-[#52525B]' 
      : 'bg-white dark:bg-[#27272A] text-[#52525B] dark:text-[#A1A1AA] ring-1 ring-[#E4E4E7] dark:ring-[#3F3F46] hover:ring-[#A1A1AA]',
  };

  return (
    <button
      onClick={onClick}
      className={`
        flex items-center gap-2 px-4 py-2 rounded-full
        font-medium text-sm transition-all duration-200
        ${colorClasses[color]}
        ${pulse ? 'animate-pulse-slow' : ''}
      `}
    >
      {Icon && <Icon className="w-4 h-4" />}
      <span className="font-semibold">{count}</span>
      <span className="opacity-80">{label}</span>
    </button>
  );
}

// ═══════════════════════════════════════════════════════════════
// QUICK STATS BAR - Replaces the large metrics panel
// ═══════════════════════════════════════════════════════════════
// QUICK STATS BAR - Simple date-based filters (no redundant breakdown)
// ═══════════════════════════════════════════════════════════════

function QuickStatsBar({ 
  orders, 
  completedOrders, 
  quickFilter, 
  setQuickFilter
}) {
  const todayStr = getPSTDateString();
  const tomorrowDate = new Date();
  tomorrowDate.setDate(tomorrowDate.getDate() + 1);
  const tomorrowStr = getPSTDateString(tomorrowDate);
  
  // Count orders by date (excluding completed for active orders)
  const activeOrders = orders.filter(o => !isTerminalOrderStatus(o.status));
  
  const ordersToday = activeOrders.filter(o => getOrderDateInPST(o.inStockDate) === todayStr).length;
  const ordersTomorrow = activeOrders.filter(o => getOrderDateInPST(o.inStockDate) === tomorrowStr).length;
  const needsAttention = activeOrders.filter((order) => needsDispatchAttention(order)).length;

  const handleStatClick = (filterType) => {
    if (quickFilter === filterType) {
      setQuickFilter(null);
    } else {
      setQuickFilter(filterType);
    }
  };

  return (
    <div className="flex items-center gap-3 flex-wrap">
      <StatPill 
        label="Today" 
        count={ordersToday} 
        color="coral"
        icon={FiZap}
        active={quickFilter === 'today'}
        onClick={() => handleStatClick('today')}
      />
      <StatPill 
        label="Tomorrow" 
        count={ordersTomorrow} 
        color="amber"
        icon={FiCalendar}
        active={quickFilter === 'tomorrow'}
        onClick={() => handleStatClick('tomorrow')}
      />
      <StatPill 
        label="Needs Attention" 
        count={needsAttention} 
        color="sky"
        icon={FiAlertCircle}
        pulse={needsAttention > 0}
        active={quickFilter === 'attention'}
        onClick={() => handleStatClick('attention')}
      />
      
      {/* Clear Filter Button - shows when any filter is active */}
      {quickFilter && (
        <button
          onClick={() => setQuickFilter(null)}
          className="flex items-center gap-1.5 px-3 py-2 rounded-full text-sm font-medium transition-all duration-200 bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-white"
        >
          <FiX className="w-4 h-4" />
          <span>Clear Filter</span>
        </button>
      )}
    </div>
  );
}

// OPS ANALYTICS STRIP — DispatchOpsStats, OpsStatCard, OpsWindowSelector and
// the isOrderWithinNextDaysPST helper live in ../components/commandCenter/opsStats
// so the Dispatch screen and Command Center hub stay in sync.

// ═══════════════════════════════════════════════════════════════
// TAB NAVIGATION - Modern pill-style tabs
// ═══════════════════════════════════════════════════════════════

function TabButton({ label, count, active, onClick, color = 'gray' }) {
  const baseClasses = "relative px-4 py-2.5 rounded-xl font-medium text-sm transition-all duration-200 flex items-center gap-2";
  
  const colorMap = {
    coral: active ? 'bg-coral-500 text-white shadow-lg shadow-coral-500/25' : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700',
    amber: active ? 'bg-amber-500 text-white shadow-lg shadow-amber-500/25' : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700',
    emerald: active ? 'bg-emerald-500 text-white shadow-lg shadow-emerald-500/25' : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700',
    violet: active ? 'bg-violet-500 text-white shadow-lg shadow-violet-500/25' : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700',
    sky: active ? 'bg-sky-500 text-white shadow-lg shadow-sky-500/25' : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700',
    gray: active ? 'bg-gray-900 dark:bg-gray-700 text-white shadow-lg' : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700',
  };
  
  return (
    <button onClick={onClick} className={`${baseClasses} ${colorMap[color]}`}>
      <span>{label}</span>
      {count > 0 && (
        <span className={`
          text-xs font-bold px-2 py-0.5 rounded-full
          ${active ? 'bg-white/20' : 'bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300'}
        `}>
          {count}
        </span>
      )}
    </button>
  );
}

// ═══════════════════════════════════════════════════════════════
// SORT DROPDOWN - Clean dropdown for sorting
// ═══════════════════════════════════════════════════════════════

function SortDropdown({ value, onChange, options }) {
  const selectedOption = options.find(o => o.value === value) || options[0];
  
  return (
    <Menu as="div" className="relative">
      <Menu.Button className="flex items-center gap-2 px-4 py-2.5 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl hover:border-gray-300 dark:hover:border-gray-600 transition-all text-sm font-medium text-gray-700 dark:text-gray-200 shadow-sm">
        <FiLayers className="w-4 h-4 text-gray-400 dark:text-gray-500" />
        <span>{selectedOption?.label || 'Sort by'}</span>
        <FiChevronDown className="w-4 h-4 text-gray-400 dark:text-gray-500" />
      </Menu.Button>
      
      <Transition
        as={Fragment}
        enter="transition ease-out duration-100"
        enterFrom="transform opacity-0 scale-95"
        enterTo="transform opacity-100 scale-100"
        leave="transition ease-in duration-75"
        leaveFrom="transform opacity-100 scale-100"
        leaveTo="transform opacity-0 scale-95"
      >
        <Menu.Items className="absolute right-0 mt-2 w-56 origin-top-right bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 focus:outline-none z-50 overflow-hidden">
          <div className="py-1">
            {options.map((option) => (
              <Menu.Item key={option.value}>
                {({ active }) => (
                  <button
                    onClick={() => onChange(option.value)}
                    className={`
                      ${active ? 'bg-gray-50 dark:bg-gray-700' : ''}
                      ${value === option.value ? 'text-sky-600 dark:text-sky-400 font-medium' : 'text-gray-700 dark:text-gray-200'}
                      flex items-center gap-3 w-full px-4 py-2.5 text-sm
                    `}
                  >
                    {option.icon && <span className="text-gray-400 dark:text-gray-500">{option.icon}</span>}
                    <span>{option.label}</span>
                    {value === option.value && <FiCheck className="ml-auto w-4 h-4 text-sky-500 dark:text-sky-400" />}
                  </button>
                )}
              </Menu.Item>
            ))}
          </div>
        </Menu.Items>
      </Transition>
    </Menu>
  );
}

// Build "View on {company}" URL from order (storeOrderConfirmationUrl, storeOrderId + company)
function getViewOnStoreUrl(order) {
  if (!order) return null;
  const url = order.storeOrderConfirmationUrl?.trim();
  if (url && (url.startsWith('http://') || url.startsWith('https://')))
    return { href: url, label: order.company || 'Store' };
  const id = order.storeOrderId?.trim();
  if (!id) return null;
  const company = order.company || 'Ralphs';
  const byCompany = {
    Ralphs: (id) => `https://www.ralphs.com/mypurchases/pending/${id}`,
    Kroger: (id) => `https://www.kroger.com/mypurchases/pending/${id}`,
    Walmart: (id) => `https://www.walmart.com/orders/${id}`,
  };
  const fn = byCompany[company];
  if (!fn) return null;
  const longId = id.match(/\d{10,}/)?.[0] || id;
  return { href: fn(longId), label: company };
}

// ═══════════════════════════════════════════════════════════════
// ORDER CARD - New timeline-style card design
// ═══════════════════════════════════════════════════════════════

function OrderCard({ 
  order, 
  addOns = [],
  hosts, 
  drivers, 
  isSelected, 
  isToday, 
  isTomorrow,
  onSelect, 
  onHover,
  onAssignDriver,
  activeTab,
  batchInfo = [],
  allOrders = [],
  isChecked = false,
  onToggleCheck,
  isLive = false,
  storeToPropertyMi = null,
  editDriverPayNode = null,
  dateBadge = null,
  onChangeStatus,
  isStatusUpdating = false,
}) {
  const host = hosts.find(h => h._id === order.property?.hostID) || {};
  const hostName = host.firstName ? `${host.firstName} ${host.lastName}` : 'N/A';
  const driver = drivers.find(d => d.id === order.driverID);
  const driverName = driver?.name || 'Unassigned';
  
  const allItems = [order, ...addOns];
  const totalItems = allItems.flatMap(o => o.products || []).reduce((sum, p) => sum + (p.quantity || 0), 0);
  // Format arrival time in PST for consistency
  const arrivalTime = order.storeArrivalTime 
    ? new Date(order.storeArrivalTime).toLocaleTimeString('en-US', { 
        hour: 'numeric', 
        minute: '2-digit',
        timeZone: 'America/Los_Angeles'
      })
    : null;

  // ─── Priority Checks (for badges and checklist) ─────────────────
  const hasStoreOrderId = order.storeOrderId?.trim().length > 0;
  const hasStoreSpend = parseFloat(order.storeSpend) > 0;
  const isPlaced = hasStoreOrderId && hasStoreSpend;
  const isDoorDelivery = isDoorDeliveryOrder(order);
  // Admin can correct status for any PAST order that hasn't reached a terminal
  // state. Door deliveries skip the driver/arrival workflow, so they should
  // always expose the status picker while still active.
  const canChangeStatus =
    activeTab !== 4 &&
    !isTerminalOrderStatus(order.status) &&
    (isPastInStockDateInPST(order.inStockDate) || isDoorDelivery);
  const hasDriver = isDoorDelivery || !!order.driverID;
  const hasEntryInstructions = hasDispatchEntryInstructions(order);
  const hasDeliveryTime = hasDispatchDeliveryTime(order);
  const hasDriverPay = parseFloat(order.driverPay) > 0;
  
  // Calculate priority level based on missing items
  const criticalIssues = !isPlaced;
  const highIssues = !hasDriver;
  const mediumIssues = !hasEntryInstructions || !hasDeliveryTime;

  // Determine card styling based on urgency
  let borderColor = 'border-l-gray-300';
  let bgColor = 'bg-white';
  let urgencyBadge = null;
  
  // Override border color based on priority issues (most critical wins)
  if (criticalIssues) {
    borderColor = 'border-l-red-500';
    bgColor = 'bg-gradient-to-r from-red-500/5 to-transparent';
  } else if (highIssues) {
    borderColor = 'border-l-orange-500';
    bgColor = 'bg-gradient-to-r from-orange-500/5 to-transparent';
  } else if (mediumIssues) {
    borderColor = 'border-l-amber-500';
    bgColor = 'bg-gradient-to-r from-amber-500/5 to-transparent';
  } else if (isToday) {
    borderColor = 'border-l-coral-500';
    bgColor = 'bg-gradient-to-r from-coral-500/5 to-transparent';
  } else if (isTomorrow) {
    borderColor = 'border-l-amber-500';
    bgColor = 'bg-gradient-to-r from-amber-500/5 to-transparent';
  }
  
  // Date-based urgency badge
  if (isToday) {
    urgencyBadge = (
      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-coral-500/10 text-coral-600 text-xs font-semibold">
        <FiZap className="w-3 h-3" /> TODAY
      </span>
    );
  } else if (isTomorrow) {
    urgencyBadge = (
      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-amber-500/10 text-amber-600 text-xs font-semibold">
        <FiCalendar className="w-3 h-3" /> TOMORROW
      </span>
    );
  }
  
  // Priority badges array
  const priorityBadges = [];
  if (!isPlaced) {
    priorityBadges.push(
      <span key="placement" className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-red-500/10 text-red-600 dark:text-red-400 text-xs font-semibold" title="Order not placed with grocer yet">
        <FiAlertCircle className="w-3 h-3" /> NEEDS PLACEMENT
      </span>
    );
  }
  if (!hasDriver) {
    priorityBadges.push(
      <span key="driver" className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-orange-500/10 text-orange-600 dark:text-orange-400 text-xs font-semibold" title="No concierge assigned">
        <FiTruck className="w-3 h-3" /> NO DRIVER
      </span>
    );
  }
  if (!hasEntryInstructions) {
    priorityBadges.push(
      <span key="entry" className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-amber-500/10 text-amber-600 dark:text-amber-400 text-xs font-semibold" title="Missing entry instructions">
        <FiLock className="w-3 h-3" /> NO ENTRY
      </span>
    );
  }
  if (!hasDeliveryTime) {
    priorityBadges.push(
      <span key="time" className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-amber-500/10 text-amber-600 dark:text-amber-400 text-xs font-semibold" title="Missing delivery time">
        <FiClock className="w-3 h-3" /> NO TIME
      </span>
    );
  }
  if (storeToPropertyMi != null && storeToPropertyMi > 15) {
    priorityBadges.push(
      <span key="distance" className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-orange-500/10 text-orange-600 dark:text-orange-400 text-xs font-semibold" title="Store is over 15 miles from property – verify address and delivery feasibility">
        <FiMapPin className="w-3 h-3" /> {storeToPropertyMi.toFixed(1)} mi – verify
      </span>
    );
  }

  return (
    <div className="group">
      {/* Main Order Card */}
      <div
        onClick={() => onSelect(order._id)}
        onMouseEnter={() => onHover(order)}
        onMouseLeave={() => onHover(null)}
        className={`
          relative rounded-2xl border-l-4 ${borderColor}
          ${bgColor} bg-white dark:bg-gray-800
          shadow-card hover:shadow-card-hover
          transition-all duration-200 cursor-pointer
          ${isSelected ? 'ring-2 ring-sky-500 ring-offset-2 dark:ring-offset-gray-900' : ''}
        `}
      >
        <div className="p-5">
          {/* Delivery type badge — same blue gradient as drawer when deliver_to_door */}
          {isDoorDelivery && (
            <div className="relative rounded-lg mb-3 overflow-hidden">
              <div className="absolute -inset-0.5 bg-gradient-to-r from-sky-400 via-blue-500 to-indigo-500 rounded-lg blur opacity-30" />
              <div className="relative bg-gradient-to-r from-sky-500 to-blue-600 rounded-lg px-3 py-2 border border-sky-400/30">
                <div className="flex items-center gap-2">
                  <div className="w-7 h-7 rounded-md flex items-center justify-center bg-white/20 shrink-0">
                    <FiTruck className="w-3.5 h-3.5 text-white" />
                  </div>
                  <div className="min-w-0 flex items-baseline gap-2">
                    <span className="font-semibold text-white text-xs tracking-wide">DELIVERY</span>
                    <span className="text-[11px] text-white/80 truncate">Deliver to door</span>
                  </div>
                </div>
              </div>
            </div>
          )}
          {/* Header Row */}
            <div className="flex items-start justify-between mb-4">
            <div className="flex items-center gap-3">
              {/* Checkbox for multi-select */}
              {onToggleCheck && (
                <input
                  type="checkbox"
                  checked={isChecked}
                  onChange={(e) => {
                    e.stopPropagation();
                    onToggleCheck(order._id);
                  }}
                  onClick={(e) => e.stopPropagation()}
                  className="w-5 h-5 rounded border-gray-300 dark:border-gray-600 text-sky-600 dark:text-sky-400 focus:ring-sky-500 cursor-pointer bg-white dark:bg-gray-700"
                />
              )}
              
              {/* Time Badge */}
              {arrivalTime && (
                <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200">
                  <FiClock className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                  <span className="font-mono font-semibold text-sm">{arrivalTime}</span>
              </div>
              )}
              
              {/* Order ID */}
              <div>
                <div className="flex items-center gap-2">
                  <span className="font-mono font-bold text-gray-900 dark:text-white text-lg">#{order.uniqueId}</span>
                  {isLive && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#D1FAE5] text-[#065F46] dark:bg-[#064E3B] dark:text-[#6EE7B7] text-xs font-semibold ring-1 ring-[#6EE7B7]/50 animate-pulse-slow">
                      <span className="w-1.5 h-1.5 rounded-full bg-[#10B981]"></span>
                      LIVE
                    </span>
                  )}
              </div>
                {order._isAddon && (
                  <span className="ml-2 text-xs font-medium text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 px-2 py-0.5 rounded-full">
                    Add-on for #{order.originalUniqueId}
                  </span>
                )}
            </div>
            </div>

            <div className="flex items-center gap-2 flex-wrap justify-end">
              {urgencyBadge}
              {dateBadge && (
                <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 text-xs font-semibold">
                  <FiCalendar className="w-3 h-3" />
                  {dateBadge}
                </span>
              )}
              {/* Priority Badges - show what's missing */}
              {priorityBadges}
              {/* More Actions */}
              <Menu as="div" className="relative">
                <Menu.Button className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors">
                  <FiMoreHorizontal className="w-5 h-5" />
                </Menu.Button>
                <Transition
                  as={Fragment}
                  enter="transition ease-out duration-100"
                  enterFrom="opacity-0 scale-95"
                  enterTo="opacity-100 scale-100"
                  leave="transition ease-in duration-75"
                  leaveFrom="opacity-100 scale-100"
                  leaveTo="opacity-0 scale-95"
                >
                  <Menu.Items className="absolute right-0 mt-1 w-48 bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 z-20 overflow-hidden">
                    <Menu.Item>
                      {({ active }) => (
                        <button className={`${active ? 'bg-gray-50 dark:bg-gray-700' : ''} flex items-center gap-2 w-full px-4 py-2.5 text-sm text-gray-700 dark:text-gray-200`}>
                          <FiRefreshCw className="w-4 h-4" /> Refresh Order
                        </button>
                      )}
                    </Menu.Item>
                  </Menu.Items>
                </Transition>
              </Menu>
          </div>
        </div>
        
          {/* Info Grid */}
          <div className="grid grid-cols-2 gap-4 mb-4">
            {/* Property */}
            <div className="flex items-start gap-3">
              <div className="p-2 rounded-lg bg-gray-100 dark:bg-gray-700">
                <FiMapPin className="w-4 h-4 text-gray-500 dark:text-gray-400" />
              </div>
              <div className="min-w-0">
                <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">Property</p>
                <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                  {order.property?.address?.line1}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {order.property?.address?.city}
                  <StatePill state={order?.property?.address?.state} />
                </p>
              </div>
            </div>

            {/* Host */}
            <div className="flex items-start gap-3">
              <div className="p-2 rounded-lg bg-gray-100 dark:bg-gray-700">
                <FiUser className="w-4 h-4 text-gray-500 dark:text-gray-400" />
            </div>
              <div className="min-w-0">
                <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">Host</p>
                <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{hostName}</p>
          </div>
        </div>
        
            {/* Items */}
            <div className="flex items-start gap-3">
              <div className="p-2 rounded-lg bg-gray-100 dark:bg-gray-700">
                <FiPackage className="w-4 h-4 text-gray-500 dark:text-gray-400" />
              </div>
              <div>
                <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">Items</p>
                <p className="text-sm font-bold text-gray-900 dark:text-white">{totalItems} items</p>
                {addOns.length > 0 && (
                  <p className="text-xs text-violet-600 dark:text-violet-400 font-medium">+{addOns.length} add-on orders</p>
                )}
              </div>
            </div>

            {/* Store */}
            <div className="flex items-start gap-3">
              <div className="p-2 rounded-lg bg-gray-100 dark:bg-gray-700">
                <FiTruck className="w-4 h-4 text-gray-500 dark:text-gray-400" />
              </div>
              <div className="min-w-0">
                <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">Store</p>
                <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                  {order.storeLocation || 'Not set'}
                </p>
                {storeToPropertyMi != null && storeToPropertyMi > 10 && (
                  <p className="text-xs font-medium text-red-600 dark:text-red-400 mt-1">
                    Verify: {storeToPropertyMi.toFixed(1)} miles away from property
                  </p>
                )}
          </div>
        </div>

            {/* Store Spend */}
            <div className="flex items-start gap-3">
              <div className="p-2 rounded-lg bg-gray-100 dark:bg-gray-700">
                <FiDollarSign className="w-4 h-4 text-gray-500 dark:text-gray-400" />
              </div>
              <div className="min-w-0">
                <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">Store Spend</p>
                <p className={`text-sm font-bold ${parseFloat(order.storeSpend) > 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-gray-400 dark:text-gray-500'}`}>
                  {parseFloat(order.storeSpend) > 0 ? `$${parseFloat(order.storeSpend).toFixed(2)}` : '–'}
                </p>
              </div>
            </div>

            {/* Driver Pay */}
            <div className="flex items-start gap-3">
              <div className="p-2 rounded-lg bg-gray-100 dark:bg-gray-700">
                <FiDollarSign className="w-4 h-4 text-gray-500 dark:text-gray-400" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">Concierge Pay</p>
                <div className="flex items-center gap-2">
                  <p className={`text-sm font-bold ${parseFloat(order.driverPay) > 0 ? 'text-sky-600 dark:text-sky-400' : 'text-gray-400 dark:text-gray-500'}`}>
                    {parseFloat(order.driverPay) > 0 ? `$${parseFloat(order.driverPay).toFixed(2)}` : '–'}
                  </p>
                  {editDriverPayNode && (
                    <div onClick={(e) => e.stopPropagation()}>
                      {editDriverPayNode}
                    </div>
                  )}
                </div>
              </div>
            </div>
      </div>

          {/* View on Store link - only when we have a store order URL */}
          {(() => {
            const viewOn = getViewOnStoreUrl(order);
            if (!viewOn) return null;
            return (
              <div className="flex items-center py-3 px-3 mb-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl">
                <a
                  href={viewOn.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-sky-500 hover:bg-sky-600 text-white text-xs font-medium rounded-lg transition-colors"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-3.5 h-3.5">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 6H5.25A2.25 2.25 0 0 0 3 8.25v10.5A2.25 2.25 0 0 0 5.25 21h10.5A2.25 2.25 0 0 0 18 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25" />
                  </svg>
                  View on {viewOn.label}
                </a>
              </div>
            );
          })()}

          {/* Footer Row */}
          <div className="flex items-center justify-between pt-4 border-t border-gray-100 dark:border-gray-700">
            <div className="flex items-center gap-3">
              {/* Driver Badge */}
              <div className={`
                inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium
                ${order.driverID 
                  ? 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-400' 
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
                }
              `}>
                <FiUser className="w-4 h-4" />
                <span>{driverName}</span>
              </div>

              {/* Arrival Date */}
              <span className="text-sm text-gray-500 dark:text-gray-400">
                {formatDate(order.inStockDate)}
              </span>
            </div>

            <div className="flex items-center gap-2">
              {/* Batch Options */}
              {batchInfo.length > 0 && !isToday && !isTerminalOrderStatus(order.status) && (
                <Popover className="relative">
                  <Popover.Button className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-violet-50 dark:bg-violet-900/20 text-violet-700 dark:text-violet-300 text-xs font-semibold hover:bg-violet-100 dark:hover:bg-violet-900/30 transition-colors">
                    <FiLayers className="w-3.5 h-3.5" />
                    {batchInfo.length} Batch {batchInfo.length > 1 ? 'Opts' : 'Opt'}
                  </Popover.Button>
                  <Transition
                    as={Fragment}
                    enter="transition ease-out duration-200"
                    enterFrom="opacity-0 translate-y-1"
                    enterTo="opacity-100 translate-y-0"
                    leave="transition ease-in duration-150"
                    leaveFrom="opacity-100 translate-y-0"
                    leaveTo="opacity-0 translate-y-1"
                  >
                    <Popover.Panel className="absolute z-20 mt-2 right-0 w-72 bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 p-3">
                      <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">Batch delivery options</h4>
                      {(() => {
                        const batchOrders = [order, ...batchInfo.map(({ id }) => allOrders.find(o => o._id === id)).filter(Boolean)];
                        const sorted = [...batchOrders].sort((a, b) => {
                          const d = new Date(a.inStockDate || 0).getTime() - new Date(b.inStockDate || 0).getTime();
                          if (d !== 0) return d;
                          const tA = a.storeArrivalTime ? new Date(a.storeArrivalTime).getTime() : 0;
                          const tB = b.storeArrivalTime ? new Date(b.storeArrivalTime).getTime() : 0;
                          return tA - tB;
                        });
                        const ord = (n) => (n === 1 ? '1st' : n === 2 ? '2nd' : n === 3 ? '3rd' : `${n}th`);
                        const fmtTime = (o) => (o.storeArrivalTime
                          ? new Date(o.storeArrivalTime).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: 'America/Los_Angeles' })
                          : '—');
                        return (
                          <ul className="space-y-2">
                            {sorted.map((o, i) => (
                              <li key={o._id} className="flex justify-between items-center py-2 px-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50 text-sm">
                                <span className="font-medium text-gray-900 dark:text-gray-100">{ord(i + 1)} Delivery</span>
                                <span className="text-gray-600 dark:text-gray-300">
                                  {formatDate(o.inStockDate)} · {fmtTime(o)}
                                </span>
                              </li>
                            ))}
                          </ul>
                        );
                      })()}
                    </Popover.Panel>
                  </Transition>
                </Popover>
              )}

              {/* Quick Actions */}
              {activeTab === 2 && isAssignDriverOrder(order, allOrders) && (
                <div onClick={e => e.stopPropagation()}>
                  <AssignDriver
                    entry={order}
                    driverData={drivers}
                    setMessage={() => {}}
                    setError={() => {}}
                  />
                </div>
              )}

              {canChangeStatus && onChangeStatus && (
                <div onClick={(e) => e.stopPropagation()}>
                  <Menu as="div" className="relative">
                    <Menu.Button
                      disabled={isStatusUpdating}
                      className={`
                        flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-colors
                        ${isStatusUpdating
                          ? 'bg-gray-100 dark:bg-gray-700 text-gray-400 dark:text-gray-500 cursor-wait'
                          : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 ring-1 ring-gray-200 dark:ring-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700'
                        }
                      `}
                    >
                      <span>{isStatusUpdating ? 'Updating...' : 'Change Status'}</span>
                      {!isStatusUpdating && <FiChevronDown className="w-3.5 h-3.5" />}
                    </Menu.Button>
                    <Transition
                      as={Fragment}
                      enter="transition ease-out duration-100"
                      enterFrom="opacity-0 scale-95"
                      enterTo="opacity-100 scale-100"
                      leave="transition ease-in duration-75"
                      leaveFrom="opacity-100 scale-100"
                      leaveTo="opacity-0 scale-95"
                    >
                      <Menu.Items className="absolute right-0 mt-2 w-44 bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 z-20 overflow-hidden">
                        <Menu.Item>
                          {({ active }) => (
                            <button
                              onClick={() => onChangeStatus(allItems, 'completed')}
                              className={`${active ? 'bg-gray-50 dark:bg-gray-700' : ''} flex items-center gap-2 w-full px-4 py-2.5 text-sm text-gray-700 dark:text-gray-200`}
                            >
                              <FiCheck className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
                              Complete
                            </button>
                          )}
                        </Menu.Item>
                        <Menu.Item>
                          {({ active }) => (
                            <button
                              onClick={() => onChangeStatus(allItems, 'canceled')}
                              className={`${active ? 'bg-gray-50 dark:bg-gray-700' : ''} flex items-center gap-2 w-full px-4 py-2.5 text-sm text-gray-700 dark:text-gray-200`}
                            >
                              <FiX className="w-4 h-4 text-red-600 dark:text-red-400" />
                              Guest Canceled
                            </button>
                          )}
                        </Menu.Item>
                      </Menu.Items>
                    </Transition>
                  </Menu>
                </div>
              )}

              {/* Expand Arrow */}
              <button className="p-2 rounded-lg hover:bg-gray-100 text-gray-400 transition-colors">
                <FiChevronRight className={`w-5 h-5 transform transition-transform ${isSelected ? 'rotate-90' : ''}`} />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Add-on Orders (connected below main order) */}
      {addOns.length > 0 && (
        <div className="ml-6 pl-4 border-l-2 border-dashed border-gray-200 dark:border-gray-700 mt-2 space-y-2">
          {addOns.map(addon => (
            <div
              key={addon._id}
              className="bg-gray-50 dark:bg-gray-800 rounded-xl p-4 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors cursor-pointer"
              onClick={() => onSelect(addon._id)}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <span className="px-2 py-0.5 rounded-full bg-violet-100 dark:bg-violet-900/20 text-violet-700 dark:text-violet-400 text-xs font-semibold">
                    ADD-ON
                  </span>
                  <span className="font-mono font-semibold text-gray-700 dark:text-gray-200">#{addon.uniqueId}</span>
                  <span className="text-gray-500 dark:text-gray-400">
                    {(addon.products || []).reduce((sum, p) => sum + (p.quantity || 0), 0)} items
                  </span>
                </div>
                <FiChevronRight className="w-4 h-4 text-gray-400 dark:text-gray-500" />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// DATE GROUP HEADER - Groups orders by date
// ═══════════════════════════════════════════════════════════════

function formatDateOnly(dateString) {
  if (!dateString) return '—';
  // Handle both "YYYY-MM-DD" and full ISO strings
  const datePart = dateString.includes('T') ? dateString.split('T')[0] : dateString;
  const [year, month, day] = datePart.split('-');
  if (!year || !month || !day) return dateString; // Fallback if parsing fails
  
  const suffix =
    ['11','12','13'].includes(day) ? 'th'
    : day.endsWith('1')               ? 'st'
    : day.endsWith('2')               ? 'nd'
    : day.endsWith('3')               ? 'rd'
    : 'th';
  const names = [
    'January','February','March','April','May','June',
    'July','August','September','October','November','December'
  ];
  return `${names[parseInt(month,10) - 1]} ${parseInt(day,10)}${suffix}, ${year}`;
}

// Day colors for collapsible cards (Today/Tomorrow fixed; others cycle)
const DAY_CARD_COLORS = [
  { bg: 'bg-coral-500/15 dark:bg-coral-900/30', border: 'border-coral-400/50 dark:border-coral-500/40', text: 'text-coral-700 dark:text-coral-300', label: 'Today' },
  { bg: 'bg-amber-500/15 dark:bg-amber-900/30', border: 'border-amber-400/50 dark:border-amber-500/40', text: 'text-amber-700 dark:text-amber-300', label: 'Tomorrow' },
  { bg: 'bg-sky-500/15 dark:bg-sky-900/30', border: 'border-sky-400/50 dark:border-sky-500/40', text: 'text-sky-700 dark:text-sky-300', label: null },
  { bg: 'bg-violet-500/15 dark:bg-violet-900/30', border: 'border-violet-400/50 dark:border-violet-500/40', text: 'text-violet-700 dark:text-violet-300', label: null },
  { bg: 'bg-emerald-500/15 dark:bg-emerald-900/30', border: 'border-emerald-400/50 dark:border-emerald-500/40', text: 'text-emerald-700 dark:text-emerald-300', label: null },
  { bg: 'bg-rose-500/15 dark:bg-rose-900/30', border: 'border-rose-400/50 dark:border-rose-500/40', text: 'text-rose-700 dark:text-rose-300', label: null },
];

function DateGroupHeader({ date, count, isToday, isTomorrow, isFirstGroup }) {
  let label = formatDateOnly(date);
  let colorClasses = 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300';
  
  if (isToday) {
    label = 'Today';
    colorClasses = 'bg-coral-500/20 dark:bg-coral-900/30 text-coral-700 dark:text-coral-300 ring-2 ring-coral-400/40';
  } else if (isTomorrow) {
    label = 'Tomorrow';
    colorClasses = 'bg-amber-500/20 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 ring-2 ring-amber-400/40';
  } else {
    colorClasses = 'bg-gray-200/80 dark:bg-gray-700/80 text-gray-800 dark:text-gray-200 ring-1 ring-gray-300 dark:ring-gray-600';
  }

  return (
    <div className={`flex items-center gap-4 py-4 ${!isFirstGroup ? 'mt-8 pt-6 border-t-2 border-gray-300 dark:border-gray-600' : ''}`}>
      <div className={`px-5 py-2.5 rounded-xl font-bold text-base ${colorClasses}`}>
        {label}
      </div>
      <div className="flex-1 h-px bg-gray-200 dark:bg-gray-700" />
      <span className="text-sm font-semibold text-gray-600 dark:text-gray-300">{count} orders</span>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// FILTER PANEL - Slide-over for advanced filters
// ═══════════════════════════════════════════════════════════════

function FilterPanel({
  isOpen,
  onClose,
  driverFilter,
  dispatcherFilter,
  dateRange,
  setDateRange,
  clearAll
}) {
  return (
    <Transition show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/20 backdrop-blur-sm" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-hidden">
          <div className="absolute inset-0 overflow-hidden">
            <div className="pointer-events-none fixed inset-y-0 right-0 flex max-w-full pl-10">
              <Transition.Child
                as={Fragment}
                enter="transform transition ease-in-out duration-300"
                enterFrom="translate-x-full"
                enterTo="translate-x-0"
                leave="transform transition ease-in-out duration-300"
                leaveFrom="translate-x-0"
                leaveTo="translate-x-full"
              >
                <Dialog.Panel className="pointer-events-auto w-screen max-w-md">
                  <div className="flex h-full flex-col bg-white dark:bg-gray-800 shadow-2xl rounded-l-3xl">
                    {/* Header */}
                    <div className="px-6 py-5 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
                        <Dialog.Title className="text-xl font-bold text-gray-900 dark:text-white">
                          Filter Orders
                        </Dialog.Title>
            <button
                          onClick={onClose}
                          className="p-2 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
            >
                          <FiX className="w-5 h-5" />
            </button>
                      </div>
          </div>

                    {/* Content */}
                    <div className="flex-1 overflow-y-auto px-6 py-6 space-y-6">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Concierge</label>
            <SelectDropdown
                          placeholder="Filter by driver..."
              options={driverFilter.options}
              value={driverFilter.value}
              onChange={driverFilter.onChange}
              isClearable
              menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
              styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
            />
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Dispatcher</label>
            <SelectDropdown
                          placeholder="Filter by dispatcher..."
              options={dispatcherFilter.options}
              value={dispatcherFilter.value}
              onChange={dispatcherFilter.onChange}
              isClearable
              menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
              styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
            />
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Date Range</label>
            <div className="grid grid-cols-2 gap-4">
              <div>
                            <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">From</label>
                <DatePicker
                  selected={dateRange.start}
                  onChange={d => setDateRange(r => ({ ...r, start: d }))}
                              className="w-full border border-gray-300 dark:border-gray-600 rounded-xl px-4 py-2.5 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:border-sky-500 focus:ring-2 focus:ring-sky-500/20 focus:outline-none"
                  placeholderText="Start date"
                />
              </div>
              <div>
                            <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">To</label>
                <DatePicker
                  selected={dateRange.end}
                  onChange={d => setDateRange(r => ({ ...r, end: d }))}
                              className="w-full border border-gray-300 dark:border-gray-600 rounded-xl px-4 py-2.5 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:border-sky-500 focus:ring-2 focus:ring-sky-500/20 focus:outline-none"
                  placeholderText="End date"
                />
                          </div>
              </div>
            </div>
          </div>

                    {/* Footer */}
                    <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex gap-3">
            <button
              onClick={clearAll}
                        className="flex-1 py-3 px-4 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded-xl font-medium hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
            >
                        Reset All
            </button>
            <button
              onClick={onClose}
                        className="flex-1 py-3 px-4 bg-gray-900 dark:bg-gray-700 text-white dark:text-gray-200 rounded-xl font-medium hover:bg-gray-800 dark:hover:bg-gray-600 transition-colors"
            >
                        Apply Filters
            </button>
          </div>
        </div>
                </Dialog.Panel>
              </Transition.Child>
      </div>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}

// ═══════════════════════════════════════════════════════════════
// MAP PREVIEW — Interactive dispatch map with clusters + routes
// ═══════════════════════════════════════════════════════════════

const MILES_PER_KM = 0.621371;
const PROX_THRESHOLD_KM = 16; // ~10 miles
const MAP_DEFAULT_CENTER = { lat: 34.0522, lng: -118.2437 };
const MAP_OTHER_DAY_COLORS = ['#60A5FA', '#A78BFA', '#34D399', '#F43F5E'];
const MAP_TODAY_COLOR = '#FF6B6B';
const MAP_TOMORROW_COLOR = '#FBBF24';
const MAP_FOCUS_COLOR = '#F97316';
const MAP_HIGHLIGHT_COLOR = '#3B82F6';

const toNum = (v) => {
  if (v === null || v === undefined) return null;
  if (typeof v === 'string' && v.trim() === '') return null;
  const n = typeof v === 'string' ? Number(v.trim()) : Number(v);
  return Number.isFinite(n) ? n : null;
};

function getStoreCoords(order) {
  if (!order) return null;
  const lat = toNum(order.storeLat);
  const lng = toNum(order.storeLng);
  if (Number.isFinite(lat) && Number.isFinite(lng)) return { lat, lng };
  const s = order.store;
  if (s && (s.lat != null || s.lng != null)) return { lat: toNum(s.lat), lng: toNum(s.lng) };
  return null;
}

function getPropertyCoords(order) {
  const addr = order?.property?.address;
  if (!addr) return null;
  const lat = toNum(addr.lat);
  const lng = toNum(addr.lng);
  if (Number.isFinite(lat) && Number.isFinite(lng)) return { lat, lng };
  return null;
}

const makeEmojiIcon = (emoji, { size = 26 } = {}) =>
  L.divIcon({
    html: `<div class="emoji" style="font-size:${size}px;line-height:${size}px">${emoji}</div>`,
    className: "emoji-icon",
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
  });

const storeEmojiIcon = makeEmojiIcon("🛒", { size: 26 });

function FitToPoints({ points, fallbackCenter = MAP_DEFAULT_CENTER }) {
  const map = useMap();
  useEffect(() => {
    if (!points || points.length === 0) {
      map.setView([fallbackCenter.lat, fallbackCenter.lng], 9);
      return;
    }
    if (points.length === 1) {
      map.setView([points[0].lat, points[0].lng], 12);
      return;
    }
    map.fitBounds(L.latLngBounds(points.map(p => [p.lat, p.lng])), { padding: [48, 48] });
  }, [map, points, fallbackCenter]);
  return null;
}

// Smoothly pans the map to a hovered order's location without forcing a zoom
// change. Keeps operations feeling connected between the list and the map.
function PanToCoord({ coord }) {
  const map = useMap();
  useEffect(() => {
    if (!coord || !Number.isFinite(coord.lat) || !Number.isFinite(coord.lng)) return;
    const current = map.getCenter();
    const target = L.latLng(coord.lat, coord.lng);
    const distanceMeters = current.distanceTo(target);
    if (distanceMeters < 20) return;
    const targetZoom = Math.max(map.getZoom(), 12);
    map.flyTo(target, targetZoom, { animate: true, duration: 0.5 });
  }, [map, coord?.lat, coord?.lng]); // eslint-disable-line react-hooks/exhaustive-deps
  return null;
}

function MapPreview({
  orders,
  focusOrder,
  hoveredOrder,
  highlightOrderIds,
  routeOrder = [],
  clusterCenter = null,
  clusterRadiusKm = null,
  isExpanded,
  onToggleExpand,
  mapLayers,
  dateColorMap,
  onSelectOrder,
  showLegend = true,
  heightClass = null,
  disableExpand = false,
}) {
  const focus = hoveredOrder || focusOrder;
  const highlightSet = highlightOrderIds && highlightOrderIds.length > 0 ? new Set(highlightOrderIds) : null;

  const orderPoints = useMemo(() => {
    return (orders || [])
      .map(o => {
        const coord = getPropertyCoords(o);
        if (!coord) return null;
        return { order: o, coord };
      })
      .filter(Boolean);
  }, [orders]);

  const storePoints = useMemo(() => {
    if (!mapLayers?.stores) return [];
    const map = new Map();
    (orders || []).forEach(o => {
      const coord = getStoreCoords(o);
      if (!coord) return;
      const key = `${coord.lat.toFixed(5)}:${coord.lng.toFixed(5)}`;
      if (!map.has(key)) {
        map.set(key, {
          coord,
          label: o.storeLocation || o.company || 'Store',
          count: 1,
        });
      } else {
        map.get(key).count += 1;
      }
    });
    return Array.from(map.values());
  }, [orders, mapLayers]);

  const focusCoord = focus ? getPropertyCoords(focus) : null;
  const focusStore = focus ? getStoreCoords(focus) : null;

  const highlightPoints = useMemo(() => {
    if (!highlightSet) return [];
    return orderPoints
      .filter(p => highlightSet.has(p.order._id))
      .map(p => p.coord);
  }, [orderPoints, highlightSet]);

  const fitPoints = useMemo(() => {
    if (highlightPoints.length > 0) return highlightPoints;
    if (focusCoord) return focusStore ? [focusCoord, focusStore] : [focusCoord];
    const base = mapLayers?.orders ? orderPoints.map(p => p.coord) : [];
    const stores = storePoints.map(s => s.coord);
    return base.length > 0 ? base : stores;
  }, [highlightPoints, focusCoord, focusStore, orderPoints, mapLayers, storePoints]);

  const routeLine = useMemo(() => {
    if (!mapLayers?.routes) return [];
    if (routeOrder.length >= 2) {
      const coords = routeOrder.map(o => getPropertyCoords(o)).filter(Boolean);
      return coords.length >= 2 ? coords : [];
    }
    if (focusCoord && focusStore) return [focusStore, focusCoord];
    return [];
  }, [mapLayers, routeOrder, focusCoord, focusStore]);

  const mapHeightClass = heightClass || (isExpanded ? 'h-[600px]' : 'h-[400px]');

  return (
    <div className={`
      relative bg-gray-100 dark:bg-gray-800 rounded-2xl overflow-hidden transition-all duration-300 border border-gray-200 dark:border-gray-700
      ${mapHeightClass}
    `}>
      {!disableExpand && (
        <button
          onClick={onToggleExpand}
          className="absolute top-4 right-4 z-10 p-2 bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm rounded-lg shadow-lg hover:bg-white dark:hover:bg-gray-800 transition-colors"
        >
          <FiChevronDown className={`w-5 h-5 text-gray-600 dark:text-gray-300 transform transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
        </button>
      )}

      {showLegend && (
        <div className="absolute top-4 left-4 z-10 flex items-center gap-2 rounded-xl bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm px-3 py-2 shadow-lg border border-gray-200/60 dark:border-gray-700/60">
          <span className="text-xs font-medium text-gray-700 dark:text-gray-200">🏠 Properties</span>
          <span className="text-xs text-gray-400">•</span>
          <span className="text-xs font-medium text-gray-700 dark:text-gray-200">🛒 Stores</span>
          {highlightSet && (
            <>
              <span className="text-xs text-gray-400">•</span>
              <span className="text-xs font-medium text-sky-600 dark:text-sky-400">Highlighted cluster</span>
            </>
          )}
        </div>
      )}

      <MapContainer
        center={[MAP_DEFAULT_CENTER.lat, MAP_DEFAULT_CENTER.lng]}
        zoom={10}
        scrollWheelZoom
        className="w-full h-full"
      >
        <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />

        {mapLayers?.orders && orderPoints.map(({ order, coord }) => {
          const dateKey = order.inStockDate ? new Date(order.inStockDate).toISOString().slice(0, 10) : 'unknown';
          const baseColor = dateColorMap?.[dateKey] || MAP_OTHER_DAY_COLORS[0];
          const isHoverFocus = hoveredOrder && order._id === hoveredOrder._id;
          const isFocus = focus && order._id === focus._id;
          const isHighlighted = highlightSet && highlightSet.has(order._id);
          const color = isFocus ? MAP_FOCUS_COLOR : isHighlighted ? MAP_HIGHLIGHT_COLOR : baseColor;
          const radius = isFocus ? 11 : isHighlighted ? 9 : 7;
          return (
            <CircleMarker
              key={order._id}
              center={[coord.lat, coord.lng]}
              radius={radius}
              pathOptions={{
                color,
                weight: isFocus ? 3 : 2,
                fillColor: color,
                fillOpacity: isFocus ? 0.95 : 0.8,
              }}
              eventHandlers={{
                click: () => onSelectOrder && onSelectOrder(order._id),
              }}
            >
              <Tooltip
                direction="top"
                offset={[0, -8]}
                opacity={1}
                permanent={!!isHoverFocus}
              >
                <div className="text-xs">
                  <div className="font-semibold">Order #{order.uniqueId}</div>
                  <div className="text-gray-500">{order.property?.address?.line1 || 'Property'}</div>
                  <div className="text-gray-400">{formatDateOnly(dateKey)}</div>
                </div>
              </Tooltip>
            </CircleMarker>
          );
        })}

        {storePoints.map((s, idx) => (
          <Marker key={`store-${idx}`} position={[s.coord.lat, s.coord.lng]} icon={storeEmojiIcon}>
            <Tooltip direction="top" offset={[0, -6]} opacity={1}>
              <div className="text-xs">
                <div className="font-semibold">{s.label}</div>
                <div className="text-gray-500">{s.count} order{s.count > 1 ? 's' : ''}</div>
              </div>
            </Tooltip>
          </Marker>
        ))}

        {mapLayers?.routes && routeLine.length >= 2 && (
          <Polyline
            positions={routeLine.map(p => [p.lat, p.lng])}
            pathOptions={{ color: MAP_HIGHLIGHT_COLOR, weight: 5, opacity: 0.8 }}
            className="route-path route-path--pulse"
          />
        )}

        {mapLayers?.routes && focusCoord && focusStore && !routeOrder.length && (
          <Polyline
            positions={[[focusStore.lat, focusStore.lng], [focusCoord.lat, focusCoord.lng]]}
            pathOptions={{ color: MAP_FOCUS_COLOR, weight: 4, opacity: 0.9 }}
            className="route-path"
          />
        )}

        {clusterCenter && clusterRadiusKm && (
          <Circle
            center={[clusterCenter.lat, clusterCenter.lng]}
            radius={Math.max(500, clusterRadiusKm * 1000)}
            pathOptions={{ color: MAP_HIGHLIGHT_COLOR, weight: 2, fillColor: MAP_HIGHLIGHT_COLOR, fillOpacity: 0.08 }}
          />
        )}

        <FitToPoints points={fitPoints} />
        {hoveredOrder ? <PanToCoord coord={getPropertyCoords(hoveredOrder)} /> : null}
      </MapContainer>

      {hoveredOrder ? (
        <div className="pointer-events-none absolute bottom-4 left-4 z-[1000] max-w-[320px] rounded-xl border border-sky-200 bg-white/95 px-3 py-2 shadow-lg backdrop-blur-sm dark:border-sky-800 dark:bg-gray-900/95">
          <div className="text-[10px] font-semibold uppercase tracking-wider text-sky-600 dark:text-sky-400">
            Hovered order
          </div>
          <div className="mt-0.5 truncate text-sm font-semibold text-gray-900 dark:text-white">
            #{hoveredOrder.uniqueId} · {hoveredOrder.property?.name || hoveredOrder.property?.address?.line1 || 'Property'}
          </div>
          <div className="truncate text-xs text-gray-500 dark:text-gray-400">
            {hoveredOrder.storeLocation || hoveredOrder.company || 'Store'}
            {hoveredOrder.storeArrivalTime ? ` · ${hoveredOrder.storeArrivalTime}` : ''}
          </div>
          {hoveredOrder.driverID ? (
            <div className="truncate text-[11px] text-gray-400 dark:text-gray-500">
              Driver assigned
            </div>
          ) : (
            <div className="truncate text-[11px] font-semibold text-red-500">
              No driver assigned
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// MAIN DISPATCH SCREEN COMPONENT
// ═══════════════════════════════════════════════════════════════

export default function DispatchScreen() {
  // ─── State ───────────────────────────────────────────────────
  const [orders, setOrders] = useState([]);
  const [activeTab, setActiveTab] = useState(0);
  const [expandedOrderId, setExpandedOrderId] = useState(null);
  const [drivers, setDrivers] = useState([]);
  const [properties, setProperties] = useState([]);
  const [hosts, setHosts] = useState([]);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [driverFilterValue, setDriverFilterValue] = useState(null);
  const [dispatcherFilterValue, setDispatcherFilterValue] = useState(null);
  const [deliverySort, setDeliverySort] = useState('asc');
  const [createdSort, setCreatedSort] = useState('desc');
  const [driverSort, setDriverSort] = useState(null);
  const [lastClicked, setLastClicked] = useState('delivery');
  const [adminUsers, setAdminUsers] = useState([]);
  const [showFilter, setShowFilter] = useState(false);
  const [dateRange, setDateRange] = useState({ start: null, end: null });
  const [hoveredCoords, setHoveredCoords] = useState(null);
  const [hoveredOrder, setHoveredOrder] = useState(null);
  const [hoveredGroup, setHoveredGroup] = useState(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const [completedOrders, setCompletedOrders] = useState([]);
  const [lastCompletedFetch, setLastCompletedFetch] = useState(null);
  const [didFetchDrivers, setDidFetchDrivers] = useState(false);
  const [didFetchProperties, setDidFetchProperties] = useState(false);
  const [hasFetchedCurrent, setHasFetchedCurrent] = useState(false);
  const [hasFetchedCompleted, setHasFetchedCompleted] = useState(false);
  const [completedSearchQuery, setCompletedSearchQuery] = useState('');
  const [isSearchingCompleted, setIsSearchingCompleted] = useState(false);
  const [globalSearch, setGlobalSearch] = useState('');
  const [searchResultsCount, setSearchResultsCount] = useState(null);
  const [statusChangeOrderId, setStatusChangeOrderId] = useState(null);
  const [searchParams] = useSearchParams();
  const VALID_DEEPLINK_FILTERS = useMemo(() => new Set([
    'delivery-time-missing', 'unplaced', 'no-driver', 'driver-sparse', 'today', 'tomorrow', 'attention'
  ]), []);
  const initialDeeplinkFilter = (() => {
    const f = searchParams.get('filter');
    return f && VALID_DEEPLINK_FILTERS.has(f) ? f : null;
  })();
  const initialDeeplinkWindow = (() => {
    const w = opsWindowFromParam(searchParams.get('days'));
    return w === undefined ? 3 : w;
  })();
  const [quickFilter, setQuickFilter] = useState(initialDeeplinkFilter);
  // Time window (in days) used to scope the order-related ops analytics. null = all.
  const [opsWindowDays, setOpsWindowDays] = useState(initialDeeplinkWindow);
  const [sortBy, setSortBy] = useState('delivery-asc');
  const [mapExpanded, setMapExpanded] = useState(false);
  const [focusedOrderIndex, setFocusedOrderIndex] = useState(0);
  
  // New features state
  const [selectedOrders, setSelectedOrders] = useState(new Set());
  const [viewType, setViewType] = useState('list'); // list, grid, timeline, map, routes
  const [alerts, setAlerts] = useState([]);
  const [connectionStatus, setConnectionStatus] = useState('connected'); // connected, disconnected, connecting
  const [expandedDateKeys, setExpandedDateKeys] = useState(new Set()); // which day cards are expanded (Today always shown expanded)
  const [liveOrderIds, setLiveOrderIds] = useState(new Set());
  const [mapLayers, setMapLayers] = useState({ stores: true, orders: true, routes: true });
  const [activityFilter, setActivityFilter] = useState('all');
  const [selectedDayKey, setSelectedDayKey] = useState('all');
  const [selectedClusterId, setSelectedClusterId] = useState(null);
  
  // EOD Review and Morning Check modes
  const [showEODReview, setShowEODReview] = useState(false);
  const [showMorningCheck, setShowMorningCheck] = useState(false);

  const orderListRef = useRef(null);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const dispatch = useDispatch();
  const { darkMode, setDarkMode } = useDarkMode();

  // Scroll to top handler
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Track scroll position to show/hide scroll to top button
  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 400);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setSelectedClusterId(null);
  }, [selectedDayKey, viewType]);
  const { token } = useSelector(s => s.login || {});
  const inquiryList = useSelector(s => s.inquiries?.list || []);
  const navigate = useNavigate();

  // Body scroll is locked by Headless UI Dialog internally when the order
  // modal is open, so no extra lock is needed here.

  // Pull inquiries for the dispatch ops stats card; refresh periodically so
  // the count stays fresh without a full page reload.
  useEffect(() => {
    dispatch(fetchInquiries());
    const id = setInterval(() => dispatch(fetchInquiries()), 60_000);
    return () => clearInterval(id);
  }, [dispatch]);

  const inquiriesCount = useMemo(() => {
    const now = new Date();
    const todayStr = getPSTDateString(now);
    let open = 0, awaiting = 0, today = 0;
    for (const i of inquiryList) {
      if (i.closed) continue;
      open += 1;
      if (!i.replied) awaiting += 1;
      if (i.createdAt && getPSTDateString(new Date(i.createdAt)) === todayStr) today += 1;
    }
    return { open, awaiting, today };
  }, [inquiryList]);

  // "Pending orders, driver-sparse" analytics:
  // - Qualifying orders: status is not completed/created/pending and not
  //   terminal (canceled/cancelled). These are active fulfilment orders.
  // - Scarce drivers: fewer than 3 drivers have a live currentAddress whose
  //   distance to the order's coord is within that driver's own deliveryRange.
  // - Coord source: property.address (preferred) or userInputAddress
  //   (geocoded lazily and cached across renders).
  const DRIVER_SPARSE_THRESHOLD = 3;

  const pendingForScarcity = useMemo(() => {
    const excludedStatuses = new Set(['completed', 'created', 'pending']);
    return (orders || []).filter(o => {
      if (!o) return false;
      if (isTerminalOrderStatus(o.status)) return false;
      if (excludedStatuses.has(o.status)) return false;
      if (!isOrderWithinNextDaysPST(o, opsWindowDays)) return false;
      return true;
    });
  }, [orders, opsWindowDays]);

  const resolvedOrderCoords = useResolvedOrderCoords(pendingForScarcity);

  const driversWithLatLng = useMemo(() => (
    (drivers || [])
      .map(d => {
        const lat = Number(d?.currentAddress?.lat);
        const lng = Number(d?.currentAddress?.lng);
        if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
        const rangeMi = Number(d?.deliveryRange);
        return {
          id: d.id || d._id,
          coord: { lat, lng },
          rangeMi: Number.isFinite(rangeMi) && rangeMi > 0 ? rangeMi : null,
        };
      })
      .filter(Boolean)
  ), [drivers]);

  const haversineKmFast = (a, b) => {
    if (!a || !b) return Number.POSITIVE_INFINITY;
    const toRad = d => (d * Math.PI) / 180;
    const R = 6371;
    const dLat = toRad(b.lat - a.lat);
    const dLng = toRad(b.lng - a.lng);
    const lat1 = toRad(a.lat);
    const lat2 = toRad(b.lat);
    const h = Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
    return 2 * R * Math.asin(Math.sqrt(h));
  };

  const driverSparseIdSet = useMemo(() => {
    const set = new Set();
    if (driversWithLatLng.length === 0) return set;
    pendingForScarcity.forEach(order => {
      const coord = resolvedOrderCoords[order._id];
      if (!coord) return;
      let nearby = 0;
      for (const d of driversWithLatLng) {
        const km = haversineKmFast(d.coord, coord);
        const distMi = km * MILES_PER_KM;
        const maxMi = d.rangeMi != null ? d.rangeMi : PROX_THRESHOLD_KM * MILES_PER_KM;
        if (distMi <= maxMi) {
          nearby += 1;
          if (nearby >= DRIVER_SPARSE_THRESHOLD) break;
        }
      }
      if (nearby < DRIVER_SPARSE_THRESHOLD) set.add(order._id);
    });
    return set;
  }, [pendingForScarcity, resolvedOrderCoords, driversWithLatLng]);

  const driverSparseUnresolved = useMemo(() => (
    pendingForScarcity.filter(o => !resolvedOrderCoords[o._id]).length
  ), [pendingForScarcity, resolvedOrderCoords]);

  const { adminInfo } = useSelector(s => s.login);
  const adminId = adminInfo?._id;

  const refreshOrders = updated => {
    setOrders(os => os.map(o => o._id === updated._id ? updated : o));
  };

  // ─── EOD Review and Morning Check handlers ─────────────────────
  const handleEODApprove = async (orderId, reviewData) => {
    try {
      await request.patch(`/orders/${orderId}/eod-review`, reviewData);
      // Update local state with the review data
      setOrders(os => os.map(o => 
        o._id === orderId ? { ...o, eodReview: reviewData } : o
      ));
      setCompletedOrders(os => os.map(o => 
        o._id === orderId ? { ...o, eodReview: reviewData } : o
      ));
    } catch (err) {
      console.error('Error approving order:', err);
      setError('Failed to approve order');
    }
  };

  const handleMorningVerify = async (orderId, checkData) => {
    try {
      await request.patch(`/orders/${orderId}/morning-check`, checkData);
      // Update local state with the check data
      setOrders(os => os.map(o => 
        o._id === orderId ? { ...o, morningCheck: checkData } : o
      ));
      setCompletedOrders(os => os.map(o => 
        o._id === orderId ? { ...o, morningCheck: checkData } : o
      ));
    } catch (err) {
      console.error('Error verifying order:', err);
      setError('Failed to verify order');
    }
  };

  const handleReportError = async (orderId, errorData) => {
    try {
      await request.patch(`/orders/${orderId}/report-error`, errorData);
      // Update local state with the error data
      setOrders(os => os.map(o => {
        if (o._id !== orderId) return o;
        const existingErrors = o.morningCheck?.errorsFound || [];
        return {
          ...o,
          morningCheck: {
            ...o.morningCheck,
            errorsFound: [...existingErrors, errorData]
          }
        };
      }));
      setCompletedOrders(os => os.map(o => {
        if (o._id !== orderId) return o;
        const existingErrors = o.morningCheck?.errorsFound || [];
        return {
          ...o,
          morningCheck: {
            ...o.morningCheck,
            errorsFound: [...existingErrors, errorData]
          }
        };
      }));
    } catch (err) {
      console.error('Error reporting error:', err);
      setError('Failed to report error');
    }
  };

  // ─── Seen Orders persistence ─────────────────────────────────
  const [seenOrders, setSeenOrders] = useState(() => {
    const stored = localStorage.getItem('seenOrders') || '[]';
    return new Set(JSON.parse(stored));
  });
  
  useEffect(() => {
    localStorage.setItem('seenOrders', JSON.stringify(Array.from(seenOrders)));
  }, [seenOrders]);

  // ─── Unread count listener ───────────────────────────────────
  useEffect(() => {
    if (!adminId) return;
    const convQuery = query(
      collection(db, "conversations"),
      where("participants", "array-contains", adminId)
    );
    const unsubscribe = onSnapshot(convQuery, snapshot => {
      const total = snapshot.docs.reduce((sum, doc) => {
        const c = doc.data().unreadCount;
        return sum + (typeof c === "number" ? c : 0);
      }, 0);
      setUnreadCount(total);
    }, err => console.error("Conversation listener error:", err));
    return unsubscribe;
  }, [adminId]);

  // ─── Fetch functions ─────────────────────────────────────────
  const fetchDrivers = async () => {
    if (didFetchDrivers) return;
    try {
      const res = await request.get('/drivers');
      setDidFetchDrivers(true);
      const mapped = res.data.map(d => ({
        id: d._id,
        name: `${d.firstName ?? ''} ${d.lastName ?? ''}`.trim() || 'Unnamed Concierge',
        email: d.email,
        phone: d.phoneNumber,
        currentAddress: d.currentAddress,
      }));
      setDrivers(mapped);
    } catch (err) {
      console.error('Error fetching drivers', err);
    }
  };

  const fetchHostsAndAdmins = async () => {
    try {
      const res = await request.get('/hosts');
      const allHosts = res.data || [];
      setHosts(allHosts);
      setAdminUsers(allHosts.filter(u => u.isAdmin));
    } catch (err) {
      console.error('Error fetching hosts and admins', err);
    }
  };

  const fetchCurrent = async () => {
    try {
      let allOrders = [];
      let skip = 0;
      let hasMore = true;
      const limit = 200;
      
      while (hasMore) {
        const params = { status: 'pending,requested,picked,paymentCompleted', limit };
        if (skip > 0) params.skip = skip;
        
        const { data: batch = [] } = await request.get('/orders', { params });
        if (!batch || batch.length === 0) { hasMore = false; break; }
        
        allOrders = [...allOrders, ...batch];
        if (batch.length < limit) { hasMore = false; } 
        else { skip += limit; }
        if (allOrders.length >= 5000) { hasMore = false; }
      }
      
      const cutoff = new Date();
      cutoff.setDate(cutoff.getDate() - 7);
      const recent = allOrders.filter(o => !o.inStockDate || new Date(o.inStockDate) >= cutoff);
      const sorted = [...recent].sort((a, b) => new Date(b.inStockDate) - new Date(a.inStockDate));
      setOrders(sorted);
      setHasFetchedCurrent(true);
    } catch (err) {
      console.error('Error fetching current orders:', err);
      try {
        const { data: current = [] } = await request.get('/orders', {
          params: { status: 'pending,requested,picked,paymentCompleted', limit: 200 }
        });
        const cutoff = new Date();
        cutoff.setDate(cutoff.getDate() - 7);
        const recent = current.filter(o => !o.inStockDate || new Date(o.inStockDate) >= cutoff);
        setOrders([...recent].sort((a, b) => new Date(b.inStockDate) - new Date(a.inStockDate)));
        setHasFetchedCurrent(true);
      } catch (fallbackErr) {
        console.error('Fallback fetch also failed:', fallbackErr);
      }
    }
  };

  const fetchCompleted = async (searchQuery = '', options = {}) => {
    try {
      if (searchQuery) {
        setIsSearchingCompleted(true);
      }
      const limit = options.limit ?? 250;
      const params = { page: 1, limit, status: 'completed', sort: 'inStockDate', order: 'desc' };
      if (searchQuery) {
        params.search = searchQuery;
      }
      const { data = [] } = await request.get('/orders/completed', { params });
      setCompletedOrders(Array.isArray(data) ? data : []);
      if (searchQuery) {
        setCompletedSearchQuery(searchQuery);
      } else {
        setCompletedSearchQuery('');
        const latest = (Array.isArray(data) && data.length > 0)
          ? data.reduce((max, o) => {
              const d = new Date(o.createdAt || o.inStockDate).toISOString();
              return d > max ? d : max;
            }, '')
          : null;
        if (latest) setLastCompletedFetch(latest);
      }
      setHasFetchedCompleted(true);
    } catch (err) {
      console.error('Error fetching completed orders:', err);
    } finally {
      setIsSearchingCompleted(false);
    }
  };

  // ─── Initial load ────────────────────────────────────────────
  useEffect(() => {
    fetchDrivers();
    fetchHostsAndAdmins();
    if (!didFetchProperties || !hasFetchedCompleted) {
      setDidFetchProperties(true);
    }
  }, []);

  useEffect(() => {
    if (activeTab === 4) {
      if (!hasFetchedCompleted) { fetchCompleted(); setHasFetchedCompleted(true); }
    } else {
      if (!hasFetchedCurrent) { fetchCurrent(); setHasFetchedCurrent(true); }
    }
  }, [activeTab]);

  // When drawer opens, ensure completed orders are loaded so driver stats (delivery count) have data.
  // Use a larger limit so we get all past completed orders for accurate per-driver delivery counts.
  useEffect(() => {
    if (expandedOrderId && !hasFetchedCompleted) {
      fetchCompleted('', { limit: 2000 });
      setHasFetchedCompleted(true);
    }
  }, [expandedOrderId, hasFetchedCompleted]);

  useEffect(() => {
    if (message) { fetchCurrent(); setMessage(''); }
  }, [message]);

  useEffect(() => {
    const interval = setInterval(() => {
      if (document.hidden) return;
      if (activeTab === 4) { fetchCompleted(); } 
      else { fetchCurrent(); }
    }, 70_000);
    return () => clearInterval(interval);
  }, [activeTab, lastCompletedFetch]);

  // ─── Backend search for Completed orders ──────────────────────
  useEffect(() => {
    // Only trigger backend search when on Completed tab
    if (activeTab !== 4) return;
    
    // Debounce the search
    const timer = setTimeout(() => {
      if (globalSearch && globalSearch.trim().length >= 2) {
        // Search backend for completed orders
        fetchCompleted(globalSearch.trim());
      } else if (!globalSearch && completedSearchQuery) {
        // Clear search - refetch without search param
        fetchCompleted();
      }
    }, 500); // 500ms debounce
    
    return () => clearTimeout(timer);
  }, [globalSearch, activeTab]);

  // ─── WebSocket Integration for Real-Time Updates ──────────────
  // Use all orders for WebSocket tracking, not just displayed ones
  const allOrdersForWebSocket = useMemo(() => {
    return [...orders, ...completedOrders];
  }, [orders, completedOrders]);

  useEffect(() => {
    if (!token) return;
    
    const socketsRef = new Map();
    // Only watch in-progress orders with a driver — never paymentCompleted, completed, or requested
    const noTrackingStatuses = ['paymentCompleted', 'completed', 'requested', 'canceled', 'cancelled'];
    const activeOrderIds = new Set(
      allOrdersForWebSocket
        .filter(o => !noTrackingStatuses.includes(o.status) && (o.driverID || o.status === 'picked'))
        .map(o => String(o._id))
    );

    activeOrderIds.forEach(orderId => {
      if (socketsRef.has(orderId)) return;
      
      try {
        const socket = createOrderSocket({ token, orderId });
        const unbind = bindOrderSocket(dispatch, socket, orderId);
        
        socket.on('order:update', (change) => {
          setOrders(prev => prev.map(o => 
            String(o._id) === orderId ? { ...o, ...change } : o
          ));
        });
        
        socket.on('order:milestone', (milestone) => {
          const order = allOrdersForWebSocket.find(o => String(o._id) === orderId);
          const newAlert = {
            type: milestone.type === 'delivered' ? 'success' : 'info',
            title: `Order #${order?.uniqueId || orderId} milestone`,
            message: `${milestone.type} at ${new Date(milestone.ts).toLocaleTimeString()}`,
            orderId: order?.uniqueId || orderId,
            timestamp: milestone.ts,
            dismissed: false,
          };
          setAlerts(prev => [newAlert, ...prev].slice(0, 50)); // Keep last 50
        });
        
        socket.on('connect', () => {
          setConnectionStatus('connected');
          setLiveOrderIds(prev => new Set([...prev, orderId]));
        });
        
        socket.on('disconnect', () => {
          setConnectionStatus('disconnected');
          setLiveOrderIds(prev => {
            const next = new Set(prev);
            next.delete(orderId);
            return next;
          });
        });
        
        socketsRef.set(orderId, { socket, unbind });
      } catch (err) {
        console.error(`Failed to create socket for order ${orderId}:`, err);
      }
    });

    return () => {
      socketsRef.forEach(({ socket, unbind }) => {
        try {
          unbind();
          socket.close();
        } catch (err) {
          console.error('Error closing socket:', err);
        }
      });
      socketsRef.clear();
    };
  }, [token, allOrdersForWebSocket, dispatch]);

  // ─── Order Categories (must be defined before Alert Generation) ─────
  const awaitingDispatch = useMemo(() => 
    orders.filter(o =>
      (o.status === 'paymentCompleted' || o.status === 'requested' || o.status === 'pending') && (!o.storeSpend || parseFloat(o.storeSpend) <= 0 || !o.storeOrderId?.trim())
    ), [orders]);
  
  const submittedOrders = useMemo(() =>
    orders.filter(o =>
      o.status === 'paymentCompleted' && !isDoorDeliveryOrder(o) && parseFloat(o.storeSpend) > 0 && o.storeOrderId?.trim()
    ), [orders]);
  
  const assignDriverOrders = useMemo(() =>
    orders.filter((o) => isAssignDriverOrder(o, orders)), [orders]);
  
  const dispatchedOrders = useMemo(() => {
    return orders.filter(o => {
      if (isTerminalOrderStatus(o.status)) return false;
      const parent = o.originalOrderId
        ? orders.find(p => String(p._id) === String(o.originalOrderId))
        : o;
      const hasDriver = !!parent?.driverID;
      const hasArrival = !!o.storeArrivalTime;
      const spendNum = parseFloat(o.storeSpend);
      const hasValidSpend = !isNaN(spendNum) && spendNum > 0;
      const hasOrderId = o.storeOrderId?.trim().length > 0;
      if (isDoorDeliveryOrder(o)) {
        return hasValidSpend && hasOrderId && ['paymentCompleted', 'pending', 'requested'].includes(o.status);
      }
      return hasDriver && hasArrival && hasValidSpend && hasOrderId;
    });
  }, [orders]);

  // ─── Filtered Tab Counts (respects quickFilter) ─────────────────
  const filteredTabCounts = useMemo(() => {
    const todayStr = getPSTDateString();
    const tomorrowDate = new Date();
    tomorrowDate.setDate(tomorrowDate.getDate() + 1);
    const tomorrowStr = getPSTDateString(tomorrowDate);
    
    // Apply date filter to each category
    const applyDateFilter = (arr) => {
      if (quickFilter === 'today') {
        return arr.filter(o => getOrderDateInPST(o.inStockDate) === todayStr);
      } else if (quickFilter === 'tomorrow') {
        return arr.filter(o => getOrderDateInPST(o.inStockDate) === tomorrowStr);
      } else if (quickFilter === 'attention') {
        return arr.filter((order) => needsDispatchAttention(order));
      } else if (quickFilter === 'delivery-time-missing') {
        return arr.filter(o => {
          if (isTerminalOrderStatus(o.status)) return false;
          if (!isOrderWithinNextDaysPST(o, opsWindowDays)) return false;
          return !hasDispatchDeliveryTime(o);
        });
      } else if (quickFilter === 'unplaced') {
        return arr.filter(o => {
          if (isTerminalOrderStatus(o.status)) return false;
          if (!isOrderWithinNextDaysPST(o, opsWindowDays)) return false;
          if (o.storeOrderId || (o.storeOrderConfirmationUrl && o.storeOrderConfirmationUrl.trim())) return false;
          if (o.storeSpend && Number(o.storeSpend) > 0) return false;
          return true;
        });
      } else if (quickFilter === 'no-driver') {
        return arr.filter(o => !isTerminalOrderStatus(o.status) && !o.driverID && isOrderWithinNextDaysPST(o, opsWindowDays));
      } else if (quickFilter === 'driver-sparse') {
        return arr.filter(o => driverSparseIdSet.has(o._id));
      }
      return arr;
    };
    
    return {
      awaitingDispatch: applyDateFilter(awaitingDispatch).length,
      submittedOrders: applyDateFilter(submittedOrders).length,
      assignDriverOrders: applyDateFilter(assignDriverOrders).length,
      dispatchedOrders: applyDateFilter(dispatchedOrders).length,
      completedOrders: quickFilter ? applyDateFilter(completedOrders).length : completedOrders.length,
    };
  }, [quickFilter, opsWindowDays, awaitingDispatch, submittedOrders, assignDriverOrders, dispatchedOrders, completedOrders, driverSparseIdSet]);

  // ─── Alert Generation ──────────────────────────────────────────
  useEffect(() => {
    const newAlerts = [];
    const now = Date.now();
    
    // Check all orders, not just displayed ones
    const allOrdersForAlerts = [...awaitingDispatch, ...submittedOrders, ...assignDriverOrders, ...dispatchedOrders, ...completedOrders];
    
    allOrdersForAlerts.forEach(order => {
      const orderDate = new Date(order.inStockDate).getTime();
      const hoursUntilDue = (orderDate - now) / (1000 * 60 * 60);
      const daysUntilDue = hoursUntilDue / 24;
      
      // URGENT PRIORITY: Orders 1 day away (highest priority)
      if (daysUntilDue >= 0.75 && daysUntilDue <= 1.25 && !order.driverID && !isTerminalOrderStatus(order.status)) {
        const hours = Math.round(hoursUntilDue);
        newAlerts.push({
          type: 'urgent',
          title: `🚨 URGENT: Order #${order.uniqueId} needs placement tomorrow`,
          message: `Due in ${hours} hours - Operations team action required`,
          orderId: order.uniqueId,
          timestamp: now,
          dismissed: false,
          priority: 'high',
        });
      }
      
      // HIGH PRIORITY: Orders 2 days away
      else if (daysUntilDue >= 1.75 && daysUntilDue <= 2.25 && !order.driverID && !isTerminalOrderStatus(order.status)) {
        const days = Math.round(daysUntilDue);
        newAlerts.push({
          type: 'warning',
          title: `⚠️ Order #${order.uniqueId} needs placement in ${days} days`,
          message: `Operations team - prepare for next-day dispatch`,
          orderId: order.uniqueId,
          timestamp: now,
          dismissed: false,
          priority: 'medium',
        });
      }
      
      // Urgent: Order due in <30 min, no driver
      else if (hoursUntilDue < 0.5 && hoursUntilDue > 0 && !order.driverID) {
        newAlerts.push({
          type: 'urgent',
          title: `⚠️ Urgent: Order #${order.uniqueId} needs concierge`,
          message: `Due in ${Math.round(hoursUntilDue * 60)} minutes`,
          orderId: order.uniqueId,
          timestamp: now,
          dismissed: false,
          priority: 'critical',
        });
      }
      
      // Warning: Order stuck without driver (1-2 days away)
      else if (daysUntilDue >= 0 && daysUntilDue <= 2 && !order.driverID && !isTerminalOrderStatus(order.status)) {
        const created = new Date(order.createdAt).getTime();
        const hoursSinceCreated = (now - created) / (1000 * 60 * 60);
        if (hoursSinceCreated > 1) {
          newAlerts.push({
            type: 'warning',
            title: `Order #${order.uniqueId} stuck without concierge`,
            message: `No concierge assigned for ${Math.round(hoursSinceCreated)} hours - ${Math.round(daysUntilDue * 24)} hours until due`,
            orderId: order.uniqueId,
            timestamp: now,
            dismissed: false,
            priority: daysUntilDue <= 1 ? 'high' : 'medium',
          });
        }
      }
      
      // High-value order
      if (parseFloat(order.storeSpend || 0) > 200 && !order.driverID && !isTerminalOrderStatus(order.status)) {
        newAlerts.push({
          type: 'info',
          title: `High-value order #${order.uniqueId}`,
          message: `$${parseFloat(order.storeSpend).toFixed(2)} - needs attention`,
          orderId: order.uniqueId,
          timestamp: now,
          dismissed: false,
          priority: 'medium',
        });
      }
    });
    
    if (newAlerts.length > 0) {
      setAlerts(prev => {
        const existingIds = new Set(prev.map(a => `${a.orderId}-${a.type}-${a.priority || ''}`));
        const unique = newAlerts.filter(a => !existingIds.has(`${a.orderId}-${a.type}-${a.priority || ''}`));
        // Sort by priority: critical > high > medium > low
        const priorityOrder = { critical: 0, high: 1, medium: 2, low: 3 };
        const sorted = [...unique, ...prev]
          .sort((a, b) => (priorityOrder[a.priority] || 3) - (priorityOrder[b.priority] || 3))
          .slice(0, 50);
        return sorted;
      });
    }
  }, [awaitingDispatch, submittedOrders, assignDriverOrders, dispatchedOrders, completedOrders]);

  // ─── Helper functions ────────────────────────────────────────
  const isDispatched = (order) => {
    if (isTerminalOrderStatus(order.status)) return false;
    const parent = order.originalOrderId
      ? orders.find(o => String(o._id) === String(order.originalOrderId))
      : order;
    const hasDriver = !!parent?.driverID;
    const hasArrival = !!order.storeArrivalTime;
    const spendNum = parseFloat(order.storeSpend);
    const hasValidSpend = !isNaN(spendNum) && spendNum > 0;
    const hasOrderId = order.storeOrderId?.trim().length > 0;
    if (isDoorDeliveryOrder(order)) {
      return hasValidSpend && hasOrderId;
    }
    return hasDriver && hasArrival && hasValidSpend && hasOrderId;
  };

  const haversineKm = (a, b) => {
    if (!a || !b) return Number.POSITIVE_INFINITY;
    const toRad = d => (d * Math.PI) / 180;
    const R = 6371;
    const dLat = toRad(b.lat - a.lat);
    const dLng = toRad(b.lng - a.lng);
    const lat1 = toRad(a.lat);
    const lat2 = toRad(b.lat);
    const h = Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
    return 2 * R * Math.asin(Math.sqrt(h));
  };

  const sortByProximity = list => {
    if (list.length < 3) return list;
    const remaining = [...list];
    const result = [remaining.shift()];
    while (remaining.length) {
      const last = result[result.length - 1];
      const lastPos = last.property?.address;
      let idx = 0;
      let best = Number.POSITIVE_INFINITY;
      remaining.forEach((o, i) => {
        const pos = o.property?.address;
        const d = haversineKm(lastPos, pos);
        if (d < best) { best = d; idx = i; }
      });
      result.push(remaining.splice(idx, 1)[0]);
    }
    return result;
  };

  const getDriverName = id => {
    const d = drivers.find(x => x.id === id);
    return d ? d.name : 'Unassigned';
  };

  // allForGrouping and familyMap - used for family/batch processing
  const allForGrouping = useMemo(() => 
    [...awaitingDispatch, ...submittedOrders, ...assignDriverOrders, ...dispatchedOrders, ...completedOrders],
    [awaitingDispatch, submittedOrders, assignDriverOrders, dispatchedOrders, completedOrders]
  );
  
  // Family grouping
  const familyMap = useMemo(() => {
    const map = {};
  allForGrouping.forEach(o => {
      const k = `${o.guestEmail}__${o.propertyID}__${new Date(o.inStockDate).toISOString().slice(0, 10)}`;
      (map[k] ||= []).push(o);
  });

    Object.values(map).forEach(group => {
      if (group.length < 2) return;
    const parent = group.reduce(
        (earliest, curr) => new Date(curr.createdAt) < new Date(earliest.createdAt) ? curr : earliest,
      group[0]
    );
    group.forEach(o => {
      o._isAddon = o._id !== parent._id;
      o.originalOrderId = parent._id;
        o.originalUniqueId = parent.uniqueId;
    });
  });
    
    return map;
  }, [allForGrouping]);

  // ─── Tab-based displayed orders ────────────────────────────────
  const baseDisplayedOrders = useMemo(() => {
    if (activeTab === 0) return awaitingDispatch;
    else if (activeTab === 1) return submittedOrders;
    else if (activeTab === 2) return assignDriverOrders;
    else if (activeTab === 4) return completedOrders;
    else return dispatchedOrders;
  }, [activeTab, awaitingDispatch, submittedOrders, assignDriverOrders, dispatchedOrders, completedOrders]);

  // ─── Search filtering ────────────────────────────────────────
  const filteredOrders = useMemo(() => {
    const todayStr = getPSTDateString();
    const tomorrowDate = new Date();
    tomorrowDate.setDate(tomorrowDate.getDate() + 1);
    const tomorrowStr = getPSTDateString(tomorrowDate);

    // If certain global ops filters are active, expand the source to include
    // all active-tab data (not just the current tab) so operators can find
    // every affected order from one place.
    const CROSS_TAB_FILTERS = new Set([
      'today',
      'tomorrow',
      'delivery-time-missing',
      'unplaced',
      'no-driver',
      'attention',
      'driver-sparse',
    ]);
    let filtered;
    if (CROSS_TAB_FILTERS.has(quickFilter)) {
      const activeStatuses = [...awaitingDispatch, ...submittedOrders, ...assignDriverOrders, ...dispatchedOrders];
      const allForFiltering = activeTab === 4 ? [...activeStatuses, ...completedOrders] : activeStatuses;
      filtered = [...allForFiltering];
    } else {
      filtered = [...baseDisplayedOrders];
    }

    // Apply quick filter
    if (quickFilter === 'today') {
      filtered = filtered.filter(o => getOrderDateInPST(o.inStockDate) === todayStr);
    } else if (quickFilter === 'tomorrow') {
      filtered = filtered.filter(o => getOrderDateInPST(o.inStockDate) === tomorrowStr);
    } else if (quickFilter === 'attention') {
      filtered = filtered.filter((order) => needsDispatchAttention(order));
    } else if (quickFilter === 'dispatched') {
      filtered = filtered.filter(o => o.storeSpend > 0 && o.storeOrderId);
    } else if (quickFilter === 'delivery-time-missing') {
      filtered = filtered.filter(o => {
        if (isTerminalOrderStatus(o.status)) return false;
        if (!isOrderWithinNextDaysPST(o, opsWindowDays)) return false;
        return !hasDispatchDeliveryTime(o);
      });
    } else if (quickFilter === 'unplaced') {
      filtered = filtered.filter(o => {
        if (isTerminalOrderStatus(o.status)) return false;
        if (!isOrderWithinNextDaysPST(o, opsWindowDays)) return false;
        if (o.storeOrderId || (o.storeOrderConfirmationUrl && o.storeOrderConfirmationUrl.trim())) return false;
        if (o.storeSpend && Number(o.storeSpend) > 0) return false;
        return true;
      });
    } else if (quickFilter === 'no-driver') {
      filtered = filtered.filter(o => !isTerminalOrderStatus(o.status) && !o.driverID && isOrderWithinNextDaysPST(o, opsWindowDays));
    } else if (quickFilter === 'driver-sparse') {
      filtered = filtered.filter(o => driverSparseIdSet.has(o._id));
    }
    
    if (globalSearch) {
      const q = globalSearch.toLowerCase().trim();
      const parsedDate = parseDateFromString(globalSearch);
      
      if (parsedDate) {
        const targetDate = parsedDate;
        targetDate.setHours(0, 0, 0, 0);
        
        filtered = filtered.filter(o => {
          if (o.inStockDate) {
            const orderDate = new Date(o.inStockDate);
            orderDate.setHours(0, 0, 0, 0);
            if (orderDate.getTime() === targetDate.getTime()) return true;
          }
          return false;
        });
      } else {
        filtered = filtered.filter(o => {
          const hostRec = hosts.find(h => h._id === o.property?.hostID) || {};
          const driverRec = drivers.find(d => d.id === o.driverID) || {};
          
          const searchIn = (value) => {
            if (value === null || value === undefined) return '';
            if (typeof value === 'object') return JSON.stringify(value);
            return String(value).toLowerCase();
          };
          
          const searchableText = [
            o.uniqueId, o.status, o.company, o.guestEmail, o.guestPhoneNumber,
            o.storeLocation, o.storeOrderId, o.dispatchNotes, o.internalNotes,
            o.property?.address?.line1, o.property?.address?.city,
            `${hostRec.firstName || ''} ${hostRec.lastName || ''}`,
            driverRec.name || '',
          ].map(searchIn).join(' ').toLowerCase();
          
          return searchableText.includes(q);
        });
      }
    }
    
    return filtered;
  }, [baseDisplayedOrders, globalSearch, hosts, drivers, quickFilter, opsWindowDays, awaitingDispatch, submittedOrders, assignDriverOrders, dispatchedOrders, completedOrders, driverSparseIdSet]);

  useEffect(() => {
    if (globalSearch) { setSearchResultsCount(filteredOrders.length); } 
    else { setSearchResultsCount(null); }
  }, [globalSearch, filteredOrders.length]);

  // ─── Sorting ─────────────────────────────────────────────────
  const sortOptions = [
    { label: 'Arriving Soon', value: 'delivery-asc', icon: '↑' },
    { label: 'Arriving Later', value: 'delivery-desc', icon: '↓' },
    { label: 'Newest First', value: 'created-desc', icon: '🕐' },
    { label: 'Oldest First', value: 'created-asc', icon: '🕐' },
    { label: 'Concierge A→Z', value: 'driver-asc', icon: '👤' },
    { label: 'Concierge Z→A', value: 'driver-desc', icon: '👤' },
  ];

  const viewOptions = [
    { key: 'list', label: 'List', icon: FiList },
    { key: 'grid', label: 'Grid', icon: FiGrid },
    { key: 'timeline', label: 'Timeline', icon: FiLayout },
    { key: 'routes', label: 'Routes', icon: FiLayers },
    { key: 'map', label: 'Map', icon: FiMap },
  ];

  useEffect(() => {
    if (sortBy === 'delivery-asc') { setDeliverySort('asc'); setLastClicked('delivery'); }
    else if (sortBy === 'delivery-desc') { setDeliverySort('desc'); setLastClicked('delivery'); }
    else if (sortBy === 'created-asc') { setCreatedSort('asc'); setLastClicked('created'); }
    else if (sortBy === 'created-desc') { setCreatedSort('desc'); setLastClicked('created'); }
    else if (sortBy === 'driver-asc') { setDriverSort('asc'); setLastClicked('driver'); }
    else if (sortBy === 'driver-desc') { setDriverSort('desc'); setLastClicked('driver'); }
  }, [sortBy]);

  // Final displayed orders after all filtering, sorting, and grouping
  const displayedOrders = useMemo(() => {
    // Start with filtered orders
    let result = [...filteredOrders];

    // Apply additional filters
  if (driverFilterValue) {
      result = result.filter(o => o.driverID === driverFilterValue.value);
  }
  if (dispatcherFilterValue) {
      result = result.filter(o => o.dispatcher === dispatcherFilterValue.value);
  }
  if (dateRange.start) {
      result = result.filter(o => new Date(o.inStockDate) >= dateRange.start);
  }
  if (dateRange.end) {
      result = result.filter(o => new Date(o.inStockDate) <= dateRange.end);
  }

    // Apply sorting
  if (lastClicked === 'driver') {
      result = [...result].sort((a, b) => {
      const dateA = new Date(a.inStockDate).getTime();
      const dateB = new Date(b.inStockDate).getTime();
        const dateCmp = deliverySort === 'asc' ? dateA - dateB : dateB - dateA;
      if (dateCmp !== 0) return dateCmp;
      const nameA = getDriverName(a.driverID).toLowerCase();
      const nameB = getDriverName(b.driverID).toLowerCase();
        return driverSort === 'asc' ? nameA.localeCompare(nameB) : nameB.localeCompare(nameA);
    });
  } else if (lastClicked === 'delivery') {
      result = [...result].sort((a, b) =>
      deliverySort === 'asc'
        ? new Date(a.inStockDate) - new Date(b.inStockDate)
        : new Date(b.inStockDate) - new Date(a.inStockDate)
    );
    const buckets = {};
      result.forEach(o => {
      const key = new Date(o.inStockDate).toISOString().slice(0, 10);
      (buckets[key] = buckets[key] || []).push(o);
    });
      result = Object.values(buckets).flatMap(dayList => sortByProximity(dayList));
    } else {
      result = [...result].sort((a, b) =>
      createdSort === 'asc'
        ? new Date(a.createdAt) - new Date(b.createdAt)
        : new Date(b.createdAt) - new Date(a.createdAt)
    );
  }

    return result;
  }, [filteredOrders, driverFilterValue, dispatcherFilterValue, dateRange.start, dateRange.end, lastClicked, deliverySort, createdSort, driverSort, getDriverName]);

  // ─── Grouping ────────────────────────────────────────────────
  const groupedMap = useMemo(() => {
    const map = {};
  displayedOrders.forEach(o => {
      const key = `${o.guestEmail}__${o.propertyID}__${new Date(o.inStockDate).toISOString().slice(0, 10)}`;
      (map[key] ||= []).push(o);
  });
    return map;
  }, [displayedOrders]);

  // Process family grouping
  useMemo(() => {
  Object.values(groupedMap).forEach(group => {
      const familyKey = `${group[0].guestEmail}__${group[0].propertyID}__${new Date(group[0].inStockDate).toISOString().slice(0, 10)}`;
    const fullFamily = familyMap[familyKey] || [];
    if (fullFamily.length < 2) return;
    const parent = fullFamily.reduce((p, o) =>
        new Date(o.createdAt) < new Date(p.createdAt) ? o : p, fullFamily[0]);
    group.forEach(o => {
      o._isAddon = o._id !== parent._id;
      o.originalUniqueId = parent.uniqueId;
    });
    group.sort((a, b) => {
      if (a._id === parent._id) return -1;
      if (b._id === parent._id) return 1;
      return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
    });
  });
    return groupedMap;
  }, [groupedMap, familyMap]);

  const groupedOrders = Object.values(groupedMap).flat();

  // ─── Batch info ──────────────────────────────────────────────
const batchInfoMap = useMemo(() => {
  const THRESHOLD_KM = 16;
  const byDay = allForGrouping.reduce((m, o) => {
      const day = new Date(o.inStockDate).toISOString().slice(0, 10);
    (m[day] ||= []).push(o);
    return m;
  }, {});

  const map = {};
  Object.values(byDay).forEach(dayOrders => {
      dayOrders.forEach((a, i) => {
        for (let j = i + 1; j < dayOrders.length; j++) {
        const b = dayOrders[j];
          const dist = haversineKm(a.property?.address, b.property?.address);
        if (dist <= THRESHOLD_KM) {
          (map[a._id] ||= []).push({ id: b._id, dist });
          (map[b._id] ||= []).push({ id: a._id, dist });
        }
      }
    });
  });
  return map;
}, [groupedOrders]);

  const patchOrder = async (id, data) => {
    try {
      await request.patch(`/orders/${id}/dispatch`, data);
      setExpandedOrderId(null);
    } catch (e) {
      console.error('Failed to update order:', e);
    }
  };

  const handleDispatchedStatusChange = async (groupOrders, nextStatus) => {
    const ordersToUpdate = (Array.isArray(groupOrders) ? groupOrders : []).filter(Boolean);
    if (ordersToUpdate.length === 0) return;

    const primaryOrderId = ordersToUpdate[0]?._id;
    const orderIds = new Set(ordersToUpdate.map(entry => String(entry._id)));
    const completedAt = new Date().toISOString();

    setStatusChangeOrderId(primaryOrderId);

    try {
      const results = await Promise.allSettled(
        ordersToUpdate.map(entry => request.patch(`/orders/${entry._id}/dispatch`, { status: nextStatus }))
      );
      const failed = results.find(result => result.status === 'rejected');
      if (failed) throw failed.reason;

      setOrders(prev => prev.filter(entry => !orderIds.has(String(entry._id))));
      setCompletedOrders(prev => {
        const remaining = prev.filter(entry => !orderIds.has(String(entry._id)));
        if (nextStatus !== 'completed') return remaining;

        const completedGroup = ordersToUpdate.map(entry => ({
          ...entry,
          status: 'completed',
          completedAt: entry.completedAt || completedAt,
        }));

        return [...completedGroup, ...remaining];
      });

      if (expandedOrderId && orderIds.has(String(expandedOrderId))) {
        setExpandedOrderId(null);
      }

      fetchCurrent();
    } catch (err) {
      console.error('Error updating dispatched order status:', err);
      alert(err?.response?.data?.error || 'Failed to update order status.');
      fetchCurrent();
      if (hasFetchedCompleted) fetchCompleted(completedSearchQuery);
    } finally {
      setStatusChangeOrderId(null);
    }
  };

  // Today/Tomorrow helpers
  const todayStr = getPSTDateString();
  const tomorrowDate = new Date();
  tomorrowDate.setDate(tomorrowDate.getDate() + 1);
  const tomorrowStr = getPSTDateString(tomorrowDate);

  // Group by date for display
  const ordersByDate = useMemo(() => {
    const byDate = {};
    Object.values(groupedMap).forEach(group => {
      const mainOrder = group[0];
      const dateKey = new Date(mainOrder.inStockDate).toISOString().slice(0, 10);
      (byDate[dateKey] ||= []).push(group);
    });
    return byDate;
  }, [groupedMap]);

  // Flat list of groups in same order as rendered (for map focus + keyboard nav)
  const displayedGroups = useMemo(() => {
    return Object.entries(ordersByDate)
      .sort(([a], [b]) => (deliverySort === 'asc' ? a.localeCompare(b) : b.localeCompare(a)))
      .flatMap(([, groups]) => groups);
  }, [ordersByDate, deliverySort]);

  const mapBaseOrders = useMemo(
    () => displayedGroups.map(group => group?.[0]).filter(Boolean),
    [displayedGroups]
  );

  const dateKeysSorted = useMemo(() => {
    return Object.keys(ordersByDate)
      .sort((a, b) => (deliverySort === 'asc' ? a.localeCompare(b) : b.localeCompare(a)));
  }, [ordersByDate, deliverySort]);

  const dateColorMap = useMemo(() => {
    const map = {};
    let otherIdx = 0;
    dateKeysSorted.forEach(key => {
      if (key === todayStr) map[key] = MAP_TODAY_COLOR;
      else if (key === tomorrowStr) map[key] = MAP_TOMORROW_COLOR;
      else {
        map[key] = MAP_OTHER_DAY_COLORS[otherIdx % MAP_OTHER_DAY_COLORS.length];
        otherIdx += 1;
      }
    });
    return map;
  }, [dateKeysSorted, todayStr, tomorrowStr]);

  useEffect(() => {
    if (selectedDayKey !== 'all' && !dateKeysSorted.includes(selectedDayKey)) {
      setSelectedDayKey('all');
    }
  }, [selectedDayKey, dateKeysSorted]);

  const ordersMissingCoords = useMemo(
    () => mapBaseOrders.filter(o => !getPropertyCoords(o)),
    [mapBaseOrders]
  );

  const proximityClustersByDay = useMemo(() => {
    const byDay = {};
    const dayBuckets = {};
    mapBaseOrders.forEach(order => {
      const coord = getPropertyCoords(order);
      if (!coord) return;
      const day = new Date(order.inStockDate).toISOString().slice(0, 10);
      (dayBuckets[day] ||= []).push({ order, coord });
    });

    Object.entries(dayBuckets).forEach(([day, nodes]) => {
      const visited = new Set();
      const clusters = [];
      for (let i = 0; i < nodes.length; i += 1) {
        if (visited.has(i)) continue;
        const queue = [i];
        visited.add(i);
        const clusterIdxs = [];
        while (queue.length) {
          const idx = queue.shift();
          clusterIdxs.push(idx);
          for (let j = 0; j < nodes.length; j += 1) {
            if (visited.has(j)) continue;
            const dist = haversineKm(nodes[idx].coord, nodes[j].coord);
            if (dist <= PROX_THRESHOLD_KM) {
              visited.add(j);
              queue.push(j);
            }
          }
        }
        const clusterOrders = clusterIdxs.map(ci => nodes[ci].order);
        const clusterCoords = clusterIdxs.map(ci => nodes[ci].coord);
        const center = {
          lat: clusterCoords.reduce((s, c) => s + c.lat, 0) / clusterCoords.length,
          lng: clusterCoords.reduce((s, c) => s + c.lng, 0) / clusterCoords.length,
        };
        let radiusKm = 0;
        clusterCoords.forEach(c => {
          radiusKm = Math.max(radiusKm, haversineKm(center, c));
        });
        clusters.push({
          id: `${day}-${clusterOrders[0]?._id || clusters.length}`,
          day,
          orders: clusterOrders,
          center,
          radiusKm,
          routeOrder: sortByProximity(clusterOrders),
        });
      }
      byDay[day] = clusters;
    });

    return byDay;
  }, [mapBaseOrders, haversineKm, sortByProximity]);

  const routeViewData = useMemo(() => {
    const activeDayKeys = selectedDayKey === 'all' ? dateKeysSorted : [selectedDayKey];
    const clusters = activeDayKeys.flatMap(day => proximityClustersByDay[day] || []);
    const nearbyClusters = clusters.filter(c => c.orders.length > 1);
    const selectedCluster = selectedClusterId
      ? clusters.find(c => c.id === selectedClusterId)
      : null;
    const dayFilteredOrders = selectedDayKey === 'all'
      ? mapBaseOrders
      : mapBaseOrders.filter(o => new Date(o.inStockDate).toISOString().slice(0, 10) === selectedDayKey);
    return { activeDayKeys, clusters, nearbyClusters, selectedCluster, dayFilteredOrders };
  }, [selectedDayKey, dateKeysSorted, proximityClustersByDay, selectedClusterId, mapBaseOrders]);

  const routeSummary = useMemo(() => {
    const soloStops = routeViewData.clusters.filter(c => c.orders.length === 1).length;
    return {
      totalStops: routeViewData.dayFilteredOrders.length,
      nearbyGroups: routeViewData.nearbyClusters.length,
      soloStops,
    };
  }, [routeViewData]);

  const focusOrder = (displayedGroups[focusedOrderIndex] || [])[0] ?? null;

  // Scroll spy: update focusedOrderIndex when the visible card changes
  const orderCardRefs = useRef({});
  const ratioRef = useRef({});
  useEffect(() => {
    ratioRef.current = {};
    const refs = orderCardRefs.current;
    const entries = Object.entries(refs).filter(([, el]) => el).map(([i, el]) => [parseInt(i, 10), el]);
    if (entries.length === 0) return;
    const io = new IntersectionObserver(
      (obsEntries) => {
        obsEntries.forEach(({ target, intersectionRatio }) => {
          const idx = target.getAttribute('data-order-index');
          if (idx != null) ratioRef.current[idx] = intersectionRatio;
        });
        const ratios = ratioRef.current;
        const best = Object.entries(ratios).reduce((a, b) => (a[1] >= b[1] ? a : b), ['0', 0]);
        const idx = parseInt(best[0], 10);
        if (!Number.isNaN(idx)) setFocusedOrderIndex(idx);
      },
      { root: null, rootMargin: '-15% 0px -70% 0px', threshold: [0, 0.1, 0.25, 0.5, 0.75, 1] }
    );
    entries.forEach(([, el]) => io.observe(el));
    return () => entries.forEach(([, el]) => io.unobserve(el));
  }, [displayedGroups.length]);

  // ─── Keyboard navigation (uses same order as displayed list) ───
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
      const maxIdx = Math.max(0, displayedGroups.length - 1);
      if (e.key === 'j' || e.key === 'ArrowDown') {
        e.preventDefault();
        setFocusedOrderIndex(prev => Math.min(prev + 1, maxIdx));
      } else if (e.key === 'k' || e.key === 'ArrowUp') {
        e.preventDefault();
        setFocusedOrderIndex(prev => Math.max(prev - 1, 0));
      } else if (e.key === ' ' || e.key === 'Enter') {
        e.preventDefault();
        const group = displayedGroups[focusedOrderIndex];
        const currentOrderId = group?.[0]?._id;
        if (currentOrderId) {
          setExpandedOrderId(expandedOrderId === currentOrderId ? null : currentOrderId);
          setSeenOrders(s => new Set(s).add(currentOrderId));
        }
      } else if (e.key === 'Escape') {
        setExpandedOrderId(null);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [displayedGroups, focusedOrderIndex, expandedOrderId]);

  // ─── Render ──────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <div className="bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800 sticky top-0 z-30">
        <div className="max-w-[1800px] mx-auto px-6 py-4">
          {/* Top Row */}
          <div className="flex items-center justify-between mb-4">
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Dispatch Center</h1>
            
            <div className="flex items-center gap-3">
              {/* Connection Status */}
              <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-gray-100 dark:bg-gray-800">
                <div className={`w-2 h-2 rounded-full ${
                  connectionStatus === 'connected' ? 'bg-[#10B981] ring-1 ring-[#059669]' :
                  connectionStatus === 'connecting' ? 'bg-[#F59E0B] ring-1 ring-[#D97706] animate-pulse' :
                  'bg-[#EF4444] ring-1 ring-[#DC2626]'
                }`} />
                <span className="text-xs font-medium text-gray-600 dark:text-gray-400">
                  {connectionStatus === 'connected' ? 'Live' : connectionStatus === 'connecting' ? 'Connecting...' : 'Offline'}
                        </span>
                      </div>

              {/* Alert Center */}
              <AlertCenter
                alerts={alerts}
                onDismiss={(idx) => {
                  setAlerts(prev => prev.map((a, i) => i === idx ? { ...a, dismissed: true } : a));
                }}
                onDismissAll={() => {
                  setAlerts(prev => prev.map(a => ({ ...a, dismissed: true })));
                }}
              />

              {/* Dark Mode Toggle */}
              <button
                onClick={() => setDarkMode(!darkMode)}
                className="p-2.5 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                title={darkMode ? 'Switch to light mode' : 'Switch to dark mode'}
              >
                {darkMode ? (
                  <FiSun className="w-5 h-5 text-gray-600 dark:text-gray-300" />
                ) : (
                  <FiMoon className="w-5 h-5 text-gray-600 dark:text-gray-300" />
                )}
              </button>

              {/* Refresh Indicator */}
                <button
                onClick={() => activeTab === 4 ? fetchCompleted() : fetchCurrent()}
                className="flex items-center gap-2 px-4 py-2 text-sm text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800 rounded-xl transition-colors"
                >
                <FiRefreshCw className="w-4 h-4" />
                <span>Refresh</span>
                </button>

            </div>
          </div>

          {/* Ops analytics (Inquiries / Delivery time missing / Unplaced /
              No driver / Driver-sparse) + the Today/3d/7d/14d/All window
              selector now live exclusively on the Command Center. Dispatch
              focuses on working the queue. */}

          {/* Quick Stats + Performance Metrics Row */}
          <div className="flex items-center justify-between flex-wrap gap-4">
            <QuickStatsBar 
              orders={orders}
              completedOrders={completedOrders}
              quickFilter={quickFilter}
              setQuickFilter={setQuickFilter}
            />
            
            {/* Operations Modes */}
            <div className="flex items-center gap-3">
                    <button
                onClick={() => setShowMorningCheck(true)}
                className="flex items-center gap-2 px-4 py-2 bg-amber-500 hover:bg-amber-600 text-white rounded-xl font-medium text-sm transition-colors shadow-md hover:shadow-lg"
                    >
                <FiSun className="w-4 h-4" />
                Morning Check
                    </button>
              <button
                onClick={() => setShowEODReview(true)}
                className="flex items-center gap-2 px-4 py-2 bg-violet-600 hover:bg-violet-700 text-white rounded-xl font-medium text-sm transition-colors shadow-md hover:shadow-lg"
              >
                <FiMoon className="w-4 h-4" />
                EOD Review
              </button>
            </div>
                  </div>
  
          {/* Performance Metrics Row */}
          <div className="flex items-center justify-end mt-4">
            <PerformanceMetrics
              orders={orders}
              drivers={drivers}
              completedOrders={completedOrders}
            />
          </div>
        </div>
      </div>

      {/* Refund Operations Queue now lives exclusively on the Command Center. */}

      {/* Main Content */}
      <div className="max-w-[1800px] mx-auto px-6 py-6">
        <div className={`flex gap-6 ${viewType === 'map' ? 'flex-col' : ''}`}>
          {/* Left Column - Orders */}
          <div className={`${viewType === 'map' ? 'hidden' : 'flex-1'} min-w-0`}>
            
            {/* Active Filter Banner - Shows prominently when filter is active */}
            {quickFilter && (
              <div className={`mb-4 p-4 rounded-xl border-2 ${
                quickFilter === 'today' ? 'bg-coral-50 dark:bg-coral-900/20 border-coral-200 dark:border-coral-800' :
                quickFilter === 'tomorrow' ? 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800' :
                'bg-sky-50 dark:bg-sky-900/20 border-sky-200 dark:border-sky-800'
              }`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    {quickFilter === 'today' && <FiZap className="w-5 h-5 text-coral-600 dark:text-coral-400" />}
                    {quickFilter === 'tomorrow' && <FiCalendar className="w-5 h-5 text-amber-600 dark:text-amber-400" />}
                    {quickFilter === 'attention' && <FiAlertCircle className="w-5 h-5 text-sky-600 dark:text-sky-400" />}
                    <div>
                      <span className={`font-semibold ${
                        quickFilter === 'today' ? 'text-coral-700 dark:text-coral-300' :
                        quickFilter === 'tomorrow' ? 'text-amber-700 dark:text-amber-300' :
                        'text-sky-700 dark:text-sky-300'
                      }`}>
                        {quickFilter === 'today' ? "Showing Today's Orders" : 
                         quickFilter === 'tomorrow' ? "Showing Tomorrow's Orders" : 
                         quickFilter === 'delivery-time-missing' ? 'Showing Orders Missing Delivery Time' :
                         quickFilter === 'unplaced' ? 'Showing Unplaced Orders' :
                         quickFilter === 'no-driver' ? 'Showing Orders Without a Driver' :
                         quickFilter === 'driver-sparse' ? 'Showing Orders With <3 Drivers In Range' :
                         "Showing Orders That Need Attention"}
                      </span>
                      {quickFilter === 'attention' && (
                        <p className="text-sm text-sky-600 dark:text-sky-400 mt-1">
                          These orders are missing entry instructions or delivery time
                        </p>
                      )}
                      {quickFilter === 'driver-sparse' && (
                        <p className="text-sm text-sky-600 dark:text-sky-400 mt-1">
                          Active orders whose property or guest-input address has fewer than 3 drivers within delivery range
                        </p>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={() => setQuickFilter(null)}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-sm transition-colors ${
                      quickFilter === 'today' ? 'bg-coral-100 dark:bg-coral-800 text-coral-700 dark:text-coral-200 hover:bg-coral-200 dark:hover:bg-coral-700' :
                      quickFilter === 'tomorrow' ? 'bg-amber-100 dark:bg-amber-800 text-amber-700 dark:text-amber-200 hover:bg-amber-200 dark:hover:bg-amber-700' :
                      'bg-sky-100 dark:bg-sky-800 text-sky-700 dark:text-sky-200 hover:bg-sky-200 dark:hover:bg-sky-700'
                    }`}
                  >
                    <FiX className="w-4 h-4" />
                    Clear Filter
                  </button>
                </div>
              </div>
            )}

            {/* Tabs - counts update based on active filter */}
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-2 p-1 bg-gray-100 dark:bg-gray-800 rounded-2xl">
                <TabButton
                  label="Awaiting Dispatch"
                  count={filteredTabCounts.awaitingDispatch}
                  active={activeTab === 0}
                  onClick={() => { setActiveTab(0); setExpandedOrderId(null); setExpandedDateKeys(new Set()); fetchCurrent(); }}
                  color="coral"
                />
                <TabButton
                  label="Awaiting Concierge Pay"
                  count={filteredTabCounts.submittedOrders}
                  active={activeTab === 1}
                  onClick={() => { setActiveTab(1); setExpandedOrderId(null); setExpandedDateKeys(new Set()); fetchCurrent(); }}
                  color="amber"
                />
                <TabButton
                  label="Assign Concierge"
                  count={filteredTabCounts.assignDriverOrders}
                  active={activeTab === 2}
                  onClick={() => { setActiveTab(2); setExpandedOrderId(null); setExpandedDateKeys(new Set()); fetchCurrent(); }}
                  color="sky"
                />
                <TabButton
                  label="Dispatched"
                  count={filteredTabCounts.dispatchedOrders}
                  active={activeTab === 3}
                  onClick={() => { setActiveTab(3); setExpandedOrderId(null); setExpandedDateKeys(new Set()); fetchCurrent(); }}
                  color="emerald"
                />
                <TabButton
                  label="Completed"
                  count={filteredTabCounts.completedOrders}
                  active={activeTab === 4}
                  onClick={() => { setActiveTab(4); setExpandedOrderId(null); setExpandedDateKeys(new Set()); fetchCompleted(); }}
                  color="violet"
                />
                    </div>
            </div>
  
            {/* Search & Filters */}
            <div className="flex items-center gap-3 mb-6 flex-wrap">
              {/* Search Input */}
              <div className="relative flex-1 min-w-[300px] max-w-xl">
                {isSearchingCompleted && activeTab === 4 ? (
                  <div className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5">
                    <div className="w-5 h-5 border-2 border-gray-300 border-t-sky-500 rounded-full animate-spin" />
                  </div>
                ) : (
                  <FiSearch className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 dark:text-gray-500" />
                )}
                <input
                  type="search"
                  value={globalSearch}
                  onChange={e => setGlobalSearch(e.target.value)}
                  placeholder={activeTab === 4 ? "Search completed orders (backend search)..." : "Search orders, dates, concierges, addresses..."}
                  className="w-full pl-12 pr-4 py-3 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl text-sm text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:border-sky-500 focus:ring-2 focus:ring-sky-500/20 focus:outline-none transition-all shadow-sm"
                />
                {isSearchingCompleted && activeTab === 4 && (
                  <span className="absolute right-4 top-1/2 -translate-y-1/2 text-xs font-medium text-sky-500 dark:text-sky-400">
                    Searching...
                  </span>
                )}
                {!isSearchingCompleted && searchResultsCount !== null && globalSearch && (
                  <span className="absolute right-4 top-1/2 -translate-y-1/2 text-xs font-medium text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded-full">
                    {searchResultsCount} results
                  </span>
                )}
              </div>

              {/* Sort Dropdown */}
              <SortDropdown
                value={sortBy}
                onChange={setSortBy}
                options={sortOptions}
              />

              {/* View Toggle */}
              <div className="flex items-center gap-1 p-1 bg-gray-100 dark:bg-gray-800 rounded-xl">
                {viewOptions.map(({ key, label, icon: Icon }) => (
                  <button
                    key={key}
                    onClick={() => setViewType(key)}
                    className={`
                      flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors
                      ${viewType === key
                        ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-soft'
                        : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'}
                    `}
                  >
                    <Icon className="w-4 h-4" />
                    <span className="hidden lg:inline">{label}</span>
                  </button>
                ))}
              </div>

              {/* Saved Views */}
              <SavedViews
                currentView={viewType}
                onViewChange={setViewType}
                currentFilters={{
                  driverFilterValue,
                  dispatcherFilterValue,
                  dateRange,
                  quickFilter,
                  globalSearch,
                  sortBy,
                }}
                onLoadView={(filters) => {
                  setDriverFilterValue(filters.driverFilterValue || null);
                  setDispatcherFilterValue(filters.dispatcherFilterValue || null);
                  setDateRange(filters.dateRange || { start: null, end: null });
                  setQuickFilter(filters.quickFilter || null);
                  setGlobalSearch(filters.globalSearch || '');
                  setSortBy(filters.sortBy || 'delivery-asc');
                }}
              />

              {/* Filter Button */}
              <button
                onClick={() => setShowFilter(true)}
                className="flex items-center gap-2 px-4 py-3 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl hover:border-gray-300 dark:hover:border-gray-600 transition-all text-sm font-medium text-gray-700 dark:text-gray-200 shadow-sm"
              >
                <FiFilter className="w-4 h-4" />
                <span>Filters</span>
                {(driverFilterValue || dispatcherFilterValue || dateRange.start || dateRange.end) && (
                  <span className="w-2 h-2 rounded-full bg-sky-500" />
                )}
                    </button>
  
                  {/* Clear Filters */}
              {(quickFilter || driverFilterValue || dispatcherFilterValue || dateRange.start || dateRange.end || globalSearch) && (
                    <button
                      onClick={() => {
                    setQuickFilter(null);
                        setDriverFilterValue(null);
                        setDispatcherFilterValue(null);
                        setDateRange({ start: null, end: null });
                    setGlobalSearch('');
                      }}
                  className="text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 underline"
                    >
                  Clear all
                    </button>
              )}
              </div>
  
            {/* View Content */}
            {viewType === 'timeline' ? (
              <ActivityTimeline
                orders={displayedOrders}
                filter={activityFilter}
              />
            ) : viewType === 'routes' ? (
              <div className="grid grid-cols-1 xl:grid-cols-[360px_1fr] gap-6">
                <div className="space-y-4">
                  <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-2xl p-4">
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Route Planner</h3>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          Groups use ~10 mile radius to show nearby stops.
                        </p>
                      </div>
                      <select
                        value={selectedDayKey}
                        onChange={(e) => setSelectedDayKey(e.target.value)}
                        className="text-xs bg-gray-100 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg px-2 py-1.5 text-gray-700 dark:text-gray-200"
                      >
                        <option value="all">All dates</option>
                        {dateKeysSorted.map(key => {
                          const label = key === todayStr
                            ? `Today (${formatDateOnly(key)})`
                            : key === tomorrowStr
                              ? `Tomorrow (${formatDateOnly(key)})`
                              : formatDateOnly(key);
                          return (
                            <option key={key} value={key}>{label}</option>
                          );
                        })}
                      </select>
                    </div>
                    <div className="mt-4 grid grid-cols-3 gap-2 text-xs">
                      <div className="rounded-lg bg-gray-100 dark:bg-gray-700 px-3 py-2 text-center">
                        <div className="text-gray-500 dark:text-gray-400">Stops</div>
                        <div className="font-semibold text-gray-900 dark:text-white">{routeSummary.totalStops}</div>
                      </div>
                      <div className="rounded-lg bg-gray-100 dark:bg-gray-700 px-3 py-2 text-center">
                        <div className="text-gray-500 dark:text-gray-400">Groups</div>
                        <div className="font-semibold text-gray-900 dark:text-white">{routeSummary.nearbyGroups}</div>
                      </div>
                      <div className="rounded-lg bg-gray-100 dark:bg-gray-700 px-3 py-2 text-center">
                        <div className="text-gray-500 dark:text-gray-400">Solo</div>
                        <div className="font-semibold text-gray-900 dark:text-white">{routeSummary.soloStops}</div>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-3">
                    {routeViewData.activeDayKeys.map(dayKey => {
                      const clusters = (proximityClustersByDay[dayKey] || []).filter(c => c.orders.length > 1);
                      if (clusters.length === 0) return null;
                      return (
                        <div key={dayKey} className="space-y-2">
                          <div className="flex items-center gap-2 text-xs font-semibold text-gray-600 dark:text-gray-300">
                            <span className="h-2 w-2 rounded-full" style={{ background: dateColorMap[dayKey] || MAP_OTHER_DAY_COLORS[0] }} />
                            {formatDateOnly(dayKey)}
                          </div>
                          {clusters.map(cluster => (
                            <button
                              key={cluster.id}
                              onClick={() => setSelectedClusterId(cluster.id)}
                              className={`w-full text-left rounded-xl border px-3 py-3 transition-all ${
                                selectedClusterId === cluster.id
                                  ? 'border-sky-400 bg-sky-50 dark:bg-sky-900/20'
                                  : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:border-gray-300 dark:hover:border-gray-600'
                              }`}
                            >
                              <div className="flex items-center justify-between">
                                <div className="text-sm font-semibold text-gray-900 dark:text-white">
                                  {cluster.orders.length} nearby orders
                                </div>
                                <div className="text-xs text-gray-500">
                                  {cluster.radiusKm.toFixed(1)} km radius
                                </div>
                              </div>
                              <div className="mt-2 flex flex-wrap gap-1">
                                {cluster.orders.slice(0, 4).map(o => (
                                  <span key={o._id} className="text-xs bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded-full px-2 py-0.5">
                                    #{o.uniqueId}
                                  </span>
                                ))}
                                {cluster.orders.length > 4 && (
                                  <span className="text-xs text-gray-400">+{cluster.orders.length - 4} more</span>
                                )}
                              </div>
                            </button>
                          ))}
                        </div>
                      );
                    })}

                    {routeViewData.nearbyClusters.length === 0 && (
                      <div className="rounded-xl border border-dashed border-gray-200 dark:border-gray-700 px-4 py-6 text-center text-sm text-gray-500 dark:text-gray-400">
                        No nearby groups found for this date range.
                      </div>
                    )}
                  </div>

                  {ordersMissingCoords.length > 0 && (
                    <div className="rounded-xl border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 px-4 py-3 text-xs text-amber-700 dark:text-amber-300">
                      <div className="font-semibold mb-1">Missing coordinates</div>
                      {ordersMissingCoords.slice(0, 4).map(o => (
                        <div key={o._id}>Order #{o.uniqueId} • {o.property?.address?.line1 || 'Unknown address'}</div>
                      ))}
                      {ordersMissingCoords.length > 4 && (
                        <div className="text-amber-600 dark:text-amber-400">+{ordersMissingCoords.length - 4} more</div>
                      )}
                    </div>
                  )}
                </div>

                <div className="space-y-4">
                  <div className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-400">
                    <span className="font-semibold text-gray-800 dark:text-gray-200">Map focus:</span>
                    <button
                      onClick={() => setSelectedClusterId(null)}
                      className="px-2 py-1 rounded-lg bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"
                    >
                      All stops
                    </button>
                    {routeViewData.selectedCluster && (
                      <span className="text-gray-500 dark:text-gray-400">
                        Highlighting {routeViewData.selectedCluster.orders.length} orders
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-3 flex-wrap text-xs text-gray-600 dark:text-gray-400">
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={mapLayers.orders}
                        onChange={e => setMapLayers(prev => ({ ...prev, orders: e.target.checked }))}
                        className="w-4 h-4 rounded border-gray-300 text-sky-600 focus:ring-sky-500"
                      />
                      Orders
                    </label>
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={mapLayers.stores}
                        onChange={e => setMapLayers(prev => ({ ...prev, stores: e.target.checked }))}
                        className="w-4 h-4 rounded border-gray-300 text-sky-600 focus:ring-sky-500"
                      />
                      Stores
                    </label>
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={mapLayers.routes}
                        onChange={e => setMapLayers(prev => ({ ...prev, routes: e.target.checked }))}
                        className="w-4 h-4 rounded border-gray-300 text-sky-600 focus:ring-sky-500"
                      />
                      Routes
                    </label>
                  </div>
                  <MapPreview
                    orders={routeViewData.dayFilteredOrders}
                    focusOrder={focusOrder}
                    hoveredOrder={hoveredOrder}
                    highlightOrderIds={routeViewData.selectedCluster?.orders.map(o => o._id)}
                    routeOrder={routeViewData.selectedCluster?.routeOrder || []}
                    clusterCenter={routeViewData.selectedCluster?.center || null}
                    clusterRadiusKm={routeViewData.selectedCluster?.radiusKm || null}
                    isExpanded
                    onToggleExpand={() => {}}
                    mapLayers={mapLayers}
                    dateColorMap={dateColorMap}
                    onSelectOrder={(id) => {
                      setExpandedOrderId(id);
                      setSeenOrders(s => new Set(s).add(id));
                    }}
                    heightClass="h-[600px]"
                    disableExpand
                  />

                  {routeViewData.selectedCluster && (
                    <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4">
                      <div className="text-sm font-semibold text-gray-900 dark:text-white">Suggested order</div>
                      <div className="mt-2 flex flex-wrap gap-2">
                        {routeViewData.selectedCluster.routeOrder.map(o => (
                          <span key={o._id} className="text-xs font-semibold text-sky-700 dark:text-sky-300 bg-sky-100 dark:bg-sky-900/30 px-2 py-1 rounded-full">
                            #{o.uniqueId}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <>
                {viewType === 'grid' ? (
                  <div ref={orderListRef} data-order-list className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {displayedGroups.map((group, index) => {
                      const [mainOrder, ...addOns] = group;
                      const dateKey = new Date(mainOrder.inStockDate).toISOString().slice(0, 10);
                      const isToday = dateKey === todayStr;
                      const isTomorrow = dateKey === tomorrowStr;
                      const dateBadge = isToday ? 'Today' : isTomorrow ? 'Tomorrow' : formatDateOnly(dateKey);
                      const batchInfo = (batchInfoMap[mainOrder._id] || [])
                        .map(({ id, dist }) => ({ id, dist }))
                        .filter(({ dist }) => dist > 0)
                        .sort((a, b) => a.dist - b.dist);
                      const storeCoord = getStoreCoords(mainOrder);
                      const propAddr = mainOrder?.property?.address;
                      const storeToPropertyMi = (storeCoord && propAddr && propAddr.lat != null && propAddr.lng != null)
                        ? haversineKm(storeCoord, propAddr) * MILES_PER_KM
                        : null;
                      return (
                        <div
                          key={mainOrder._id}
                          ref={el => { orderCardRefs.current[index] = el; }}
                          data-order-index={index}
                          className="h-full"
                        >
                          <OrderCard
                            order={mainOrder}
                            addOns={addOns}
                            storeToPropertyMi={storeToPropertyMi}
                            hosts={hosts}
                            drivers={drivers}
                            isSelected={expandedOrderId === mainOrder._id}
                            isToday={isToday && activeTab !== 4}
                            isTomorrow={isTomorrow && activeTab !== 4}
                            dateBadge={dateBadge}
                            onSelect={(id) => {
                              setExpandedOrderId(expandedOrderId === id ? null : id);
                              setSeenOrders(s => new Set(s).add(id));
                            }}
                            onHover={(order) => {
                              setHoveredOrder(order || null);
                              if (order) {
                                const c = { lat: order.property?.address?.lat, lng: order.property?.address?.lng };
                                setHoveredCoords(c.lat && c.lng ? c : null);
                              } else {
                                setHoveredCoords(null);
                              }
                            }}
                            onAssignDriver={() => {}}
                            activeTab={activeTab}
                            batchInfo={batchInfo}
                            allOrders={allForGrouping}
                            isChecked={selectedOrders.has(mainOrder._id)}
                            onToggleCheck={(id) => {
                              setSelectedOrders(prev => {
                                const next = new Set(prev);
                                if (next.has(id)) next.delete(id);
                                else next.add(id);
                                return next;
                              });
                            }}
                            isLive={liveOrderIds.has(String(mainOrder._id))}
                            onChangeStatus={handleDispatchedStatusChange}
                            isStatusUpdating={statusChangeOrderId === mainOrder._id}
                            editDriverPayNode={isFullAdmin(adminInfo) ? (
                              <ChangeDeliveryFee
                                entry={mainOrder}
                                setMessage={setMessage}
                                driverData={drivers}
                                onSuccess={() => fetchCurrent()}
                              />
                            ) : null}
                          />
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div ref={orderListRef} data-order-list className="space-y-4">
                    {(() => {
                      let flatIndex = 0;
                      const sortedEntries = Object.entries(ordersByDate)
                        .sort(([a], [b]) => deliverySort === 'asc' ? a.localeCompare(b) : b.localeCompare(a));
                      return sortedEntries.map(([dateKey, groups], dateIndex) => {
                        const isToday = dateKey === todayStr;
                        const isTomorrow = dateKey === tomorrowStr;
                        const isFirstGroup = dateIndex === 0;
                        const dayColorIndex = Math.min(dateIndex, DAY_CARD_COLORS.length - 1);
                        const dayColor = DAY_CARD_COLORS[dayColorIndex];
                        const isExpanded = isToday || expandedDateKeys.has(dateKey);
                        const toggleDate = () => {
                          setExpandedDateKeys(prev => {
                            const next = new Set(prev);
                            if (next.has(dateKey)) next.delete(dateKey);
                            else next.add(dateKey);
                            return next;
                          });
                        };
                        const dayLabel = isToday ? 'Today' : isTomorrow ? 'Tomorrow' : formatDateOnly(dateKey);

                        const ordersBlock = (
                          <div className="space-y-3">
                            {groups.map((group) => {
                              const [mainOrder, ...addOns] = group;
                              const orderFlatIndex = flatIndex++;
                              const batchInfo = (batchInfoMap[mainOrder._id] || [])
                                .map(({ id, dist }) => ({ id, dist }))
                                .filter(({ dist }) => dist > 0)
                                .sort((a, b) => a.dist - b.dist);

                              const storeCoord = getStoreCoords(mainOrder);
                              const propAddr = mainOrder?.property?.address;
                              const storeToPropertyMi = (storeCoord && propAddr && propAddr.lat != null && propAddr.lng != null)
                                ? haversineKm(storeCoord, propAddr) * MILES_PER_KM
                                : null;

                              return (
                                <div
                                  key={mainOrder._id}
                                  ref={el => { orderCardRefs.current[orderFlatIndex] = el; }}
                                  data-order-index={orderFlatIndex}
                                >
                                  <OrderCard
                                    order={mainOrder}
                                    addOns={addOns}
                                    storeToPropertyMi={storeToPropertyMi}
                                    hosts={hosts}
                                    drivers={drivers}
                                    isSelected={expandedOrderId === mainOrder._id}
                                    isToday={isToday && activeTab !== 4}
                                    isTomorrow={isTomorrow && activeTab !== 4}
                                    onSelect={(id) => {
                                      setExpandedOrderId(expandedOrderId === id ? null : id);
                                      setSeenOrders(s => new Set(s).add(id));
                                    }}
                                    onHover={(order) => {
                                      setHoveredOrder(order || null);
                                      if (order) {
                                        const c = { lat: order.property?.address?.lat, lng: order.property?.address?.lng };
                                        setHoveredCoords(c.lat && c.lng ? c : null);
                                      } else {
                                        setHoveredCoords(null);
                                      }
                                    }}
                                    onAssignDriver={() => {}}
                                    activeTab={activeTab}
                                    batchInfo={batchInfo}
                                    allOrders={allForGrouping}
                                    isChecked={selectedOrders.has(mainOrder._id)}
                                    onToggleCheck={(id) => {
                                      setSelectedOrders(prev => {
                                        const next = new Set(prev);
                                        if (next.has(id)) next.delete(id);
                                        else next.add(id);
                                        return next;
                                      });
                                    }}
                                    isLive={liveOrderIds.has(String(mainOrder._id))}
                                    onChangeStatus={handleDispatchedStatusChange}
                                    isStatusUpdating={statusChangeOrderId === mainOrder._id}
                                    editDriverPayNode={isFullAdmin(adminInfo) ? (
                                      <ChangeDeliveryFee
                                        entry={mainOrder}
                                        setMessage={setMessage}
                                        driverData={drivers}
                                        onSuccess={() => fetchCurrent()}
                                      />
                                    ) : null}
                                  />
                                </div>
                              );
                            })}
                          </div>
                        );

                        if (isToday) {
                          return (
                            <div key={dateKey}>
                              <DateGroupHeader
                                date={dateKey}
                                count={groups.length}
                                isToday={isToday && activeTab !== 4}
                                isTomorrow={isTomorrow && activeTab !== 4}
                                isFirstGroup={isFirstGroup}
                              />
                              {ordersBlock}
                            </div>
                          );
                        }

                        return (
                          <div
                            key={dateKey}
                            className={`rounded-2xl border-2 ${dayColor.border} ${dayColor.bg} overflow-hidden`}
                          >
                            <button
                              type="button"
                              onClick={toggleDate}
                              className="w-full flex items-center gap-4 px-5 py-4 text-left hover:opacity-90 transition-opacity"
                            >
                              <span className={`font-bold text-base ${dayColor.text}`}>{dayLabel}</span>
                              <div className="flex-1 h-px bg-current opacity-20" />
                              <span className={`text-sm font-semibold ${dayColor.text}`}>{groups.length} orders</span>
                              {isExpanded ? (
                                <FiChevronDown className={`w-5 h-5 flex-shrink-0 ${dayColor.text}`} aria-hidden />
                              ) : (
                                <FiChevronRight className={`w-5 h-5 flex-shrink-0 ${dayColor.text}`} aria-hidden />
                              )}
                            </button>
                            {isExpanded && (
                              <div className="px-4 pb-4 pt-0">
                                {ordersBlock}
                              </div>
                            )}
                          </div>
                        );
                      });
                    })()}
                  </div>
                )}

                {/* Empty State */}
                {displayedGroups.length === 0 && (
                  <div className="text-center py-16">
                    <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 dark:bg-gray-800 rounded-2xl flex items-center justify-center">
                      <FiPackage className="w-8 h-8 text-gray-400 dark:text-gray-500" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">No orders found</h3>
                    <p className="text-gray-500 dark:text-gray-400">
                      {globalSearch || quickFilter ? 'Try adjusting your search or filters' : 'Orders will appear here when they come in'}
                    </p>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Right Column - Map */}
          {(viewType === 'list' || viewType === 'grid') && (
            <div className="w-[400px] flex-shrink-0">
              <div className="sticky top-24">
                {/* Map Layer Controls */}
                <div className="mb-3 flex items-center gap-2 flex-wrap">
                  <label className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-400">
                    <input
                      type="checkbox"
                      checked={mapLayers.orders}
                      onChange={e => setMapLayers(prev => ({ ...prev, orders: e.target.checked }))}
                      className="w-4 h-4 rounded border-gray-300 text-sky-600 focus:ring-sky-500"
                    />
                    Orders
                  </label>
                  <label className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-400">
                    <input
                      type="checkbox"
                      checked={mapLayers.stores}
                      onChange={e => setMapLayers(prev => ({ ...prev, stores: e.target.checked }))}
                      className="w-4 h-4 rounded border-gray-300 text-sky-600 focus:ring-sky-500"
                    />
                    Stores
                  </label>
                  <label className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-400">
                    <input
                      type="checkbox"
                      checked={mapLayers.routes}
                      onChange={e => setMapLayers(prev => ({ ...prev, routes: e.target.checked }))}
                      className="w-4 h-4 rounded border-gray-300 text-sky-600 focus:ring-sky-500"
                    />
                    Routes
                  </label>
                </div>
                
                <MapPreview
                  orders={mapBaseOrders}
                  focusOrder={focusOrder}
                  hoveredOrder={hoveredOrder}
                  isExpanded={mapExpanded}
                  onToggleExpand={() => setMapExpanded(!mapExpanded)}
                  mapLayers={mapLayers}
                  dateColorMap={dateColorMap}
                  onSelectOrder={(id) => {
                    setExpandedOrderId(id);
                    setSeenOrders(s => new Set(s).add(id));
                  }}
                />
              </div>
            </div>
          )}
          
          {/* Map-Focused View */}
          {viewType === 'map' && (
            <div className="w-full">
              <div className="mb-4 flex items-center gap-3">
                <div className="flex items-center gap-2">
                  <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                    <input
                      type="checkbox"
                      checked={mapLayers.orders}
                      onChange={e => setMapLayers(prev => ({ ...prev, orders: e.target.checked }))}
                      className="w-4 h-4 rounded border-gray-300 text-sky-600 focus:ring-sky-500"
                    />
                    Orders
                  </label>
                  <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                    <input
                      type="checkbox"
                      checked={mapLayers.stores}
                      onChange={e => setMapLayers(prev => ({ ...prev, stores: e.target.checked }))}
                      className="w-4 h-4 rounded border-gray-300 text-sky-600 focus:ring-sky-500"
                    />
                    Stores
                  </label>
                  <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                    <input
                      type="checkbox"
                      checked={mapLayers.routes}
                      onChange={e => setMapLayers(prev => ({ ...prev, routes: e.target.checked }))}
                      className="w-4 h-4 rounded border-gray-300 text-sky-600 focus:ring-sky-500"
                                />
                    Routes
                  </label>
                              </div>
              </div>
              <div className="h-[600px]">
                <MapPreview
                  orders={mapBaseOrders}
                  focusOrder={focusOrder}
                  hoveredOrder={hoveredOrder}
                  isExpanded={true}
                  onToggleExpand={() => {}}
                  mapLayers={mapLayers}
                  dateColorMap={dateColorMap}
                  onSelectOrder={(id) => {
                    setExpandedOrderId(id);
                    setSeenOrders(s => new Set(s).add(id));
                  }}
                  heightClass="h-[600px]"
                  disableExpand
                />
                            </div>
            </div>
                          )}
        </div>
                        </div>
                        
      {/* Filter Panel */}
      <FilterPanel
        isOpen={showFilter}
        onClose={() => setShowFilter(false)}
        driverFilter={{
          options: drivers.map(d => ({ label: d.name, value: d.id })),
          value: driverFilterValue,
          onChange: setDriverFilterValue
        }}
        dispatcherFilter={{
          options: adminUsers.map(u => ({
            label: `${u.firstName} ${u.lastName}`,
            value: `${u.firstName} ${u.lastName}`
          })),
          value: dispatcherFilterValue,
          onChange: setDispatcherFilterValue
        }}
        dateRange={dateRange}
        setDateRange={setDateRange}
        clearAll={() => {
          setDriverFilterValue(null);
          setDispatcherFilterValue(null);
          setDateRange({ start: null, end: null });
        }}
      />

      {/* Chat Panel removed — no chat overlay on Dispatch. */}

      {/* Order modal — DispatchOrderDrawer is a self-contained Headless UI
          Dialog (centered popup). We must NOT wrap it in another Dialog /
          slide-over / backdrop-blur here, or we double-stack overlays which
          blurs the modal and makes the backdrop intercept all clicks. */}
      {expandedOrderId !== null && (() => {
        const o = groupedOrders.find(g => g._id === expandedOrderId);
        if (!o) return null;
        const key = `${o.guestEmail}__${o.propertyID}__${new Date(o.inStockDate).toISOString().slice(0, 10)}`;
        const group = groupedMap[key] || [];
        if (group.length === 0) return null;
        const mainOrder = group[0];
        const addons = group.slice(1);
        return (
          <DispatchOrderDrawer
            key={mainOrder._id}
            order={mainOrder}
            isOpen={true}
            addons={addons}
            refreshOrders={refreshOrders}
            closeDrawer={() => setExpandedOrderId(null)}
            drivers={drivers}
            properties={properties}
            patchOrder={patchOrder}
            showBroadcast={activeTab === 2}
            showEditDriverPay={activeTab === 1 && isFullAdmin(adminInfo)}
            onDriverPaySet={() => {
              fetchCurrent();
              setExpandedOrderId(null);
            }}
            completedOrders={completedOrders}
          />
        );
      })()}

      {/* EOD Review Mode */}
      {showEODReview && (
        <EODReviewMode
          orders={orders}
          completedOrders={completedOrders}
          onApproveOrder={handleEODApprove}
          onClose={() => setShowEODReview(false)}
        />
      )}

      {/* Morning Check Mode */}
      {showMorningCheck && (
        <MorningCheckMode
          orders={orders}
          completedOrders={completedOrders}
          onVerifyOrder={handleMorningVerify}
          onReportError={handleReportError}
          onClose={() => setShowMorningCheck(false)}
        />
      )}

      {/* Scroll to Top Button */}
      <Transition
        show={showScrollTop}
        enter="transition-opacity duration-200"
        enterFrom="opacity-0"
        enterTo="opacity-100"
        leave="transition-opacity duration-150"
        leaveFrom="opacity-100"
        leaveTo="opacity-0"
      >
        <button
          onClick={scrollToTop}
          className="fixed bottom-6 right-6 z-50 p-3 bg-gray-900 dark:bg-white text-white dark:text-gray-900 rounded-full shadow-lg hover:bg-gray-800 dark:hover:bg-gray-100 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
          aria-label="Scroll to top"
        >
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-5 h-5">
            <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 15.75l7.5-7.5 7.5 7.5" />
          </svg>
        </button>
      </Transition>
    </div>
  );
}
